/**
 * In-browser esbuild-wasm bundler for live preview.
 * Initializes esbuild once, caches build results, orchestrates plugins.
 */
import * as esbuild from 'esbuild-wasm';
import { virtualFsPlugin, sessionFilesToPathMap } from './esbuildPluginVirtualFs';
import { cdnResolvePlugin, buildImportMap } from './esbuildPluginCdnResolve';
import { frameworkTransformPlugin } from './esbuildPluginFrameworks';
import { assembleHtml } from './esbuildHtmlAssembler';
import {
  SHADCN_BADGE,
  SHADCN_INPUT,
  SHADCN_SIDEBAR,
  SHADCN_THEME_PROVIDER,
  getShadcnBundledFiles,
  getShadcnCssVariables,
} from '../helpers/shadcnBundle';

const PREVIEW_VERBOSE_LOGS =
  typeof globalThis !== 'undefined' &&
  Boolean((globalThis as any).__TASKING_PREVIEW_DEBUG__);

// Disable bundler-side source repair so preview reflects actual session files.
const ENABLE_PREVIEW_SOURCE_FALLBACKS = false;

function previewVerboseLog(...args: unknown[]) {
  if (!PREVIEW_VERBOSE_LOGS) return;
  console.log(...args);
}

const SAFE_HEADER_COMPONENT = `import * as React from 'react';

export interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export function Header({ title = 'Tasking.tech', subtitle = 'Live Preview' }: HeaderProps) {
  return (
    <header className="w-full border-b border-border bg-background/95 px-4 py-3">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold">{title}</h1>
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    </header>
  );
}

export default Header;
`;

const JSX_VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
]);

function selfCloseVoidJsxTags(source: string): string {
  return source.replace(
    /<([A-Za-z][A-Za-z0-9-]*)\b((?:[^>"'`{]|"[^"]*"|'[^']*'|`[^`]*`|\{[^}]*\})*)>/g,
    (match, tagName: string, attrs: string) => {
      const lowerTag = tagName.toLowerCase();
      if (!JSX_VOID_TAGS.has(lowerTag)) return match;
      if (/\/\s*$/.test(attrs)) return match;
      return `<${tagName}${attrs} />`;
    },
  );
}

function repairNearestVoidTag(lines: string[], fromLineIdx: number, openingTag: string): boolean {
  const lowerTag = openingTag.toLowerCase();
  if (!JSX_VOID_TAGS.has(lowerTag)) return false;

  for (let startIdx = fromLineIdx; startIdx >= 0 && startIdx >= fromLineIdx - 80; startIdx--) {
    const startLine = lines[startIdx];
    const openTagRe = new RegExp(`<${lowerTag}(?=\\s|>|/)`, 'i');
    if (!openTagRe.test(startLine)) continue;

    for (let endIdx = startIdx; endIdx < lines.length && endIdx <= startIdx + 80; endIdx++) {
      const currentLine = lines[endIdx];
      const closePos = currentLine.lastIndexOf('>');
      if (closePos === -1) continue;
      const beforeClose = currentLine.slice(0, closePos);
      if (/\/\s*$/.test(beforeClose)) break;
      lines[endIdx] = `${beforeClose} />${currentLine.slice(closePos + 1)}`;
      return true;
    }
  }

  return false;
}

// ── Singleton initializer ──
let initialized = false;
let initPromise: Promise<void> | null = null;

export async function initEsbuild(): Promise<void> {
  if (initialized) return;
  if (initPromise) return initPromise;

  // Add 10s timeout to prevent infinite hanging
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('esbuild initialization timed out after 10s')), 10000);
  });

  initPromise = Promise.race([
    esbuild.initialize({
      wasmURL: '/assets/wasm/esbuild.wasm',
      worker: true,
    }),
    timeoutPromise,
  ])
    .then(() => {
      initialized = true;
      console.debug('[esbuild] Initialized successfully');
    })
    .catch((err) => {
      initPromise = null; // allow retry on failure
      console.error('[esbuild] Initialization failed:', err);
      throw err;
    });

  return initPromise;
}

// ── Build result cache ──
const buildCache = new Map<string, BundleResult>();
// Must match the query in esbuildPluginCdnResolve.ts
const FRAMEWORK_EXTERNALS_QUERY = 'external=react,react-dom,react-dom/client,vue,svelte';

function hashContent(files: Record<string, string>, entryPoint: string): string {
  let h = 0;
  const keys = Object.keys(files || {}).sort();
  for (const k of keys) {
    for (let i = 0; i < k.length; i++) h = ((h << 5) - h + k.charCodeAt(i)) | 0;
    const v = files[k];
    for (let i = 0; i < v.length; i++) h = ((h << 5) - h + v.charCodeAt(i)) | 0;
  }
  for (let i = 0; i < entryPoint.length; i++) h = ((h << 5) - h + entryPoint.charCodeAt(i)) | 0;
  return h.toString(36);
}

// ── Types ──
export interface BundleOptions {
  files: Array<{ fileName: string; filePath?: string | null; content?: string | null }>;
  framework?: 'react' | 'vue' | 'svelte' | 'angular' | 'vanilla' | 'unknown';
}

export interface BundleResult {
  html: string;
  errors: Array<{ text: string }>;
  warnings: Array<{ text: string }>;
}

// ── Entry point detection ──
function detectEntryPoint(fileMap: Record<string, string>): string {
  // 1. Parse index.html for <script type="module" src="...">
  const indexHtml = fileMap['/index.html'];
  if (indexHtml) {
    const match = indexHtml.match(
      /<script[^>]*type\s*=\s*["']module["'][^>]*src\s*=\s*["']([^"']+)["']/,
    );
    if (match) {
      const src = match[1].replace(/^\.?\/?/, '/');
      if (fileMap[src]) return src;
    }
  }

  // 2. Common entry points
  const candidates = [
    '/src/main.tsx',
    '/src/main.ts',
    '/src/main.jsx',
    '/src/main.js',
    '/src/index.tsx',
    '/src/index.ts',
    '/src/index.jsx',
    '/src/index.js',
    '/src/App.tsx',
    '/src/App.jsx',
    '/src/App.vue',
    '/src/App.svelte',
    '/main.tsx',
    '/main.ts',
    '/main.jsx',
    '/main.js',
  ];
  for (const c of candidates) {
    if (fileMap[c]) return c;
  }

  // 3. Any JSX/TSX/Vue/Svelte file
  for (const p of Object.keys(fileMap)) {
    if (p.match(/\.(tsx|jsx|vue|svelte)$/)) return p;
  }

  // 4. Fallback only if the file actually exists
  const fallbackEntry = '/src/main.tsx';
  if (fileMap[fallbackEntry]) return fallbackEntry;

  // No recognizable entry point - return empty string to trigger graceful error
  return '';
}

/** Unwrap @layer blocks: `@layer base { body { color: red; } }` → `body { color: red; }` */
function unwrapAtLayer(css: string): string {
  const re = /@layer\s+\w+\s*\{/g;
  let match;
  let result = css;
  // Process from last match to first to preserve offsets
  const matches: Array<{ start: number; end: number }> = [];
  while ((match = re.exec(css)) !== null) {
    const openIdx = match.index + match[0].length - 1; // position of opening {
    let depth = 1;
    let i = openIdx + 1;
    while (i < css.length && depth > 0) {
      if (css[i] === '{') depth++;
      else if (css[i] === '}') depth--;
      i++;
    }
    if (depth === 0) {
      matches.push({ start: match.index, end: i });
    }
  }
  // Replace from end to start
  for (let j = matches.length - 1; j >= 0; j--) {
    const { start, end } = matches[j];
    const header = css.slice(start, css.indexOf('{', start) + 1);
    const inner = css.slice(start + header.length, end - 1); // between { and }
    result = result.slice(0, start) + inner + result.slice(end);
  }
  return result;
}

function detectHasTailwind(fileMap: Record<string, string>): boolean {
  // shadcn/ui ALWAYS requires Tailwind — short-circuit if we detect shadcn component paths or imports
  const hasShadcnPaths =
    Object.keys(fileMap).some((p) => p.includes('/components/ui/')) ||
    Object.values(fileMap).some((content) => content.includes('@/components/ui/'));
  if (hasShadcnPaths) return true;

  // cn/clsx/twMerge utility imports are a strong signal of Tailwind usage
  const hasTwUtils = Object.values(fileMap).some(
    (content) =>
      /\bimport\b.*\b(?:cn|clsx|twMerge|cva)\b/.test(content) ||
      /\bfrom\s+["'].*(?:class-variance-authority|clsx|tailwind-merge)["']/.test(content),
  );
  if (hasTwUtils) return true;

  // Check package.json dependencies
  const pkgContent = fileMap['/package.json'];
  let hasTailwindInPkg = false;
  if (pkgContent) {
    try {
      const pkg = JSON.parse(pkgContent);
      const deps = { ...pkg.dependencies, ...pkg.devDependencies };
      hasTailwindInPkg = 'tailwindcss' in deps;
    } catch {
      /* ignore */
    }
  }
  // Also check CSS files for Tailwind v4 imports, v3 directives, or v4-only at-rules (@theme, @custom-variant)
  const hasTailwindInCSS = Object.entries(fileMap).some(
    ([path, content]) =>
      path.endsWith('.css') &&
      (content.includes('@import "tailwindcss"') ||
        content.includes("@import 'tailwindcss'") ||
        content.includes('@tailwind base') ||
        content.includes('@tailwind utilities') ||
        content.includes('@theme') || // Tailwind v4 @theme at-rule
        content.includes('@custom-variant')), // Tailwind v4 @custom-variant
  );
  // Also check JSX/TSX/HTML for strong Tailwind v4 utility class signals.
  // Scan anywhere in the file content (not just className="...") to catch cn(), clsx(),
  // template literals, and other dynamic className patterns.
  if (!hasTailwindInPkg && !hasTailwindInCSS) {
    const codeFiles = Object.entries(fileMap).filter(([path]) => /\.(tsx?|jsx?|html)$/.test(path));
    const twPatterns = [
      // Numeric scale colors (bg-blue-500, text-slate-900, border-red-300)
      /\b(?:bg|text|border)-(?:slate|gray|zinc|neutral|stone|red|orange|amber|yellow|lime|green|emerald|teal|cyan|sky|blue|indigo|violet|purple|fuchsia|pink|rose)-\d{2,3}\b/,
      // Spacing utilities (p-4, mx-2, mt-8)
      /\b(?:p|px|py|pt|pb|pl|pr|m|mx|my|mt|mb|ml|mr)-\d+\b/,
      // Rounded/shadow with scale (rounded-lg, shadow-md)
      /\b(?:rounded|shadow)-(?:sm|md|lg|xl|2xl|3xl|full)\b/,
      // Layout (flex, grid, gap, justify, items, space)
      /\b(?:flex-col|flex-row|grid-cols|gap-\d|justify-center|justify-between|items-center|items-start|space-[xy]-\d)\b/,
      // Size (w-full, h-screen, max-w-2xl)
      /\b(?:w-full|h-full|h-screen|min-h-screen|max-w-\w+)\b/,
      // shadcn/ui semantic tokens (bg-primary, text-foreground, border-border, bg-card, bg-muted)
      /\b(?:bg|text|border|ring|fill|stroke)-(?:primary|secondary|accent|muted|card|popover|foreground|background|destructive|border|input|ring|chart)\b/,
    ];
    let matchCount = 0;
    for (const pattern of twPatterns) {
      if (codeFiles.some(([, content]) => pattern.test(content))) {
        matchCount++;
      }
    }
    // 2+ distinct patterns = likely Tailwind project (lower threshold to catch shadcn-only sessions)
    if (matchCount >= 2) {
      return true;
    }
  }
  return hasTailwindInPkg || hasTailwindInCSS;
}

function detectHasAngular(fileMap: Record<string, string>): boolean {
  const pkgContent = fileMap['/package.json'];
  if (!pkgContent) return false;
  try {
    const pkg = JSON.parse(pkgContent);
    const deps = { ...pkg.dependencies, ...pkg.devDependencies };
    return '@angular/core' in deps;
  } catch {
    return false;
  }
}

function isBareSpecifier(spec: string): boolean {
  if (!spec) return false;
  if (spec.startsWith('.') || spec.startsWith('/') || spec.startsWith('#')) return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(spec)) return false;
  return true;
}

function withFrameworkExternals(url: string): string {
  if (/\/react(?:@|\/)|\/react-dom(?:@|\/)|\/vue(?:@|\/)|\/svelte(?:@|\/)/.test(url)) return url;
  if (url.includes(FRAMEWORK_EXTERNALS_QUERY)) return url;
  if (url.includes('external=react,react-dom,react-dom/client')) {
    return url.replace('external=react,react-dom,react-dom/client', FRAMEWORK_EXTERNALS_QUERY);
  }
  return `${url}${url.includes('?') ? '&' : '?'}${FRAMEWORK_EXTERNALS_QUERY}`;
}

function getBasePackageName(spec: string): string {
  if (spec.startsWith('@')) {
    const [scope, name] = spec.split('/');
    return scope && name ? `${scope}/${name}` : spec;
  }
  return spec.split('/')[0];
}

function isValidNpmPackageName(name: string): boolean {
  return /^(?:@[-\w.]+\/[-\w.]+|[-\w.]+)$/.test(name);
}

function isValidBareImportSpecifier(spec: string): boolean {
  if (!isBareSpecifier(spec)) return false;
  // Skip app-local aliases and malformed values (e.g. "@/...", " : /", etc.).
  if (spec.startsWith('@/')) return false;
  if (/\s/.test(spec)) return false;
  if (spec.endsWith('/') || spec.endsWith('\\')) return false;
  return /^(?:@[-\w.]+\/[-\w.]+|[-\w.]+)(?:\/[-\w./]+)?$/.test(spec);
}

function augmentImportMapFromSourceFiles(
  fileMap: Record<string, string>,
  imports: Record<string, string>,
): void {
  // Parse only source-like files to avoid false positives from markdown/chat/package text.
  const SOURCE_FILE_REGEX = /\.(?:[cm]?[jt]sx?|vue|svelte)$/i;
  // Match actual ESM import/export statements.
  const staticImportRegex =
    /(?:^|[\n\r])\s*(?:import|export)\s+(?:[^'";]+?\s+from\s*)?["']([^"']+)["']/g;
  const dynamicImportRegex = /(?:^|[\n\r])\s*import\(\s*["']([^"']+)["']\s*\)/g;

  for (const [path, content] of Object.entries(fileMap)) {
    if (!SOURCE_FILE_REGEX.test(path)) continue;
    let match: RegExpExecArray | null;

    staticImportRegex.lastIndex = 0;
    while ((match = staticImportRegex.exec(content)) !== null) {
      const spec = match[1].trim();
      if (!isValidBareImportSpecifier(spec) || imports[spec]) continue;
      imports[spec] = withFrameworkExternals(`https://esm.sh/${spec}`);
      const basePkg = getBasePackageName(spec);
      if (isValidNpmPackageName(basePkg) && !imports[`${basePkg}/`]) {
        // Prefix mappings must end with "/" and cannot include query params.
        imports[`${basePkg}/`] = `https://esm.sh/${basePkg}/`;
      }
    }

    dynamicImportRegex.lastIndex = 0;
    while ((match = dynamicImportRegex.exec(content)) !== null) {
      const spec = match[1].trim();
      if (!isValidBareImportSpecifier(spec) || imports[spec]) continue;
      imports[spec] = withFrameworkExternals(`https://esm.sh/${spec}`);
      const basePkg = getBasePackageName(spec);
      if (isValidNpmPackageName(basePkg) && !imports[`${basePkg}/`]) {
        imports[`${basePkg}/`] = `https://esm.sh/${basePkg}/`;
      }
    }
  }
}

function addImportMapEntryForSpecifier(spec: string, imports: Record<string, string>): void {
  if (!isValidBareImportSpecifier(spec) || imports[spec]) return;
  imports[spec] = withFrameworkExternals(`https://esm.sh/${spec}`);
  const basePkg = getBasePackageName(spec);
  if (isValidNpmPackageName(basePkg) && !imports[`${basePkg}/`]) {
    // Prefix mappings must end with "/" and cannot include query params.
    imports[`${basePkg}/`] = `https://esm.sh/${basePkg}/`;
  }
}

function augmentImportMapFromBundledJs(bundledJs: string, imports: Record<string, string>): void {
  // Safety net: parse final bundled JS for any remaining bare imports.
  // This catches edge cases introduced by transforms where source scanning misses a specifier.
  const staticImportRegex =
    /(?:^|[\n\r])\s*(?:import|export)\s+(?:[^'";]+?\s+from\s*)?["']([^"']+)["']/g;
  const dynamicImportRegex = /import\(\s*["']([^"']+)["']\s*\)/g;
  let match: RegExpExecArray | null;

  staticImportRegex.lastIndex = 0;
  while ((match = staticImportRegex.exec(bundledJs)) !== null) {
    addImportMapEntryForSpecifier(match[1].trim(), imports);
  }

  dynamicImportRegex.lastIndex = 0;
  while ((match = dynamicImportRegex.exec(bundledJs)) !== null) {
    addImportMapEntryForSpecifier(match[1].trim(), imports);
  }
}

// ── SvelteKit synthetic entry ──
// SvelteKit projects use +page.svelte / +layout.svelte instead of main.ts.
// If no explicit entry point exists, inject a synthetic main.ts that mounts
// the SvelteKit component tree.
function injectSvelteKitEntry(fileMap: Record<string, string>): void {
  // Skip if an explicit entry already exists
  const hasExplicitEntry = [
    '/src/main.tsx',
    '/src/main.ts',
    '/src/main.jsx',
    '/src/main.js',
    '/src/index.tsx',
    '/src/index.ts',
    '/src/index.jsx',
    '/src/index.js',
    '/src/App.svelte',
    '/main.ts',
    '/main.js',
  ].some((p) => fileMap[p]);
  if (hasExplicitEntry || !ENABLE_PREVIEW_SOURCE_FALLBACKS) return;

  // Check for SvelteKit-style files
  const svelteKitFiles = Object.keys(fileMap).filter(
    (p) => p.includes('+page.svelte') || p.includes('+layout.svelte'),
  );
  if (svelteKitFiles.length === 0) return;

  // Find the main +page.svelte (prefer /src/routes/+page.svelte)
  const pagePaths = svelteKitFiles.filter((p) => p.endsWith('+page.svelte'));
  const layoutPaths = svelteKitFiles.filter((p) => p.endsWith('+layout.svelte'));

  // Pick the root page
  let mainPage =
    pagePaths.find((p) => p === '/src/routes/+page.svelte') ||
    pagePaths.find((p) => p.includes('/routes/+page.svelte')) ||
    pagePaths[0];

  // Pick the root layout
  const mainLayout =
    layoutPaths.find((p) => p === '/src/routes/+layout.svelte') ||
    layoutPaths.find((p) => p.includes('/routes/+layout.svelte')) ||
    layoutPaths[0];

  if (!mainPage && !mainLayout) return;

  // Generate synthetic entry
  const imports: string[] = [];
  let mountCode: string;

  if (mainLayout && mainPage) {
    imports.push(`import Layout from '${mainLayout}';`);
    imports.push(`import Page from '${mainPage}';`);
    // Svelte 4: mount layout with page as slot content
    mountCode = `
const target = document.getElementById('app') || document.getElementById('root') || document.body;
try {
  // Try Svelte 5 mount first
  const { mount } = await import('svelte');
  mount(Layout, { target, props: { children: (anchor) => mount(Page, { target: anchor }) } });
} catch(e) {
  // Fall back to Svelte 4 constructor
  new Layout({ target, props: { $$slots: { default: [() => new Page({ target: document.createElement('div') })] } } });
}`;
  } else {
    const comp = mainPage || mainLayout!;
    imports.push(`import Component from '${comp}';`);
    mountCode = `
const target = document.getElementById('app') || document.getElementById('root') || document.body;
try {
  const { mount } = await import('svelte');
  mount(Component, { target });
} catch(e) {
  new Component({ target });
}`;
  }

  const syntheticEntry = `${imports.join('\n')}\n${mountCode}`;
  fileMap['/src/main.js'] = syntheticEntry;
}

// ── React synthetic entry ──
// When App.tsx/App.jsx exists but no main.tsx/main.ts entry, inject a synthetic
// main.tsx that imports and renders the App component with createRoot.
// AI-generated projects sometimes create App.tsx without a corresponding main.tsx,
// or generate a main.tsx that doesn't import App.
function injectReactEntry(fileMap: Record<string, string>): void {
  // Skip if an explicit main entry already exists
  const mainEntries = [
    '/src/main.tsx',
    '/src/main.ts',
    '/src/main.jsx',
    '/src/main.js',
    '/src/index.tsx',
    '/src/index.ts',
    '/src/index.jsx',
    '/src/index.js',
    '/main.tsx',
    '/main.ts',
    '/main.jsx',
    '/main.js',
  ];
  const existingMain = mainEntries.find((p) => fileMap[p]);

  // Find App component
  const appPath = fileMap['/src/App.tsx']
    ? '/src/App.tsx'
    : fileMap['/src/App.jsx']
      ? '/src/App.jsx'
      : null;

  if (!appPath || !ENABLE_PREVIEW_SOURCE_FALLBACKS) return; // No App component to mount

  if (existingMain) {
    // main exists — verify it imports App. If not, patch the import in.
    const mainSrc = fileMap[existingMain];
    const importsApp =
      /import\s+.*(?:App|default).*from\s+['"]\.\/App['"]/.test(mainSrc) ||
      /import\s+.*(?:App|default).*from\s+['"]\.\.\/App['"]/.test(mainSrc) ||
      /import\s+App/.test(mainSrc) ||
      /from\s+['"][^'"]*App[^'"]*['"]/.test(mainSrc);
    if (importsApp) return; // Already imports App

    // Check if it renders anything meaningful (has createRoot or ReactDOM.render)
    const hasRender = /createRoot|ReactDOM\.render|render\s*\(/.test(mainSrc);
    if (!hasRender) {
      // main.tsx exists but doesn't import App or render anything — replace it
      console.debug('[bundler] main.tsx exists but missing App import, injecting synthetic entry');
    } else {
      // Has render logic but somehow doesn't import App — add the import
      const appImportPath = existingMain.startsWith('/src/') ? './App' : './src/App';
      fileMap[existingMain] = `import App from '${appImportPath}';\n` + mainSrc;
      console.debug('[bundler] Patched App import into', existingMain);
      return;
    }
  }

  // Generate synthetic main.tsx
  const cssImport = fileMap['/src/index.css'] ? "import './index.css';\n" : '';
  const syntheticMain = `${cssImport}import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const root = ReactDOM.createRoot(
  document.getElementById('root') || document.getElementById('app') || document.body
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`;
  fileMap['/src/main.tsx'] = syntheticMain;
  console.debug('[bundler] Injected synthetic React main.tsx for', appPath);
}

/**
 * Parse esbuild error messages and apply targeted fixes to problematic lines.
 * Returns true if any fixes were applied (caller should retry the build).
 */
function autoFixBuildErrors(fileMap: Record<string, string>, errorMessage: string): boolean {
  const errorRe = /virtual-fs:(?:virtual-fs:)?(\/[^:\n]+):(\d+):(\d+):\s*ERROR:\s*(.+)/g;
  const bundledFiles = getShadcnBundledFiles();
  const fallbackButton = bundledFiles['src/components/ui/button.tsx'] || '';
  const fallbackCard = bundledFiles['src/components/ui/card.tsx'] || '';
  const fallbackBadge = bundledFiles['src/components/ui/badge.tsx'] || SHADCN_BADGE;
  const fallbackInput = bundledFiles['src/components/ui/input.tsx'] || SHADCN_INPUT;
  let anyFixed = false;
  let match;

  while ((match = errorRe.exec(errorMessage)) !== null) {
    const [, filePathRaw, lineStr, colStr, errorText] = match;
    const filePath =
      '/' + filePathRaw.replace(/^virtual-fs:/i, '').replace(/^\/+/, '').replace(/\\/g, '/');
    const content = fileMap[filePath];
    if (!content) continue;

    const lineNum = parseInt(lineStr, 10);
    const colNum = parseInt(colStr, 10);
    const lines = content.split('\n');
    if (lineNum < 1 || lineNum > lines.length) continue;

    const lineIdx = lineNum - 1;
    const originalLine = lines[lineIdx];
    const indent = originalLine.match(/^(\s*)/)?.[1] ?? '';
    const trimmed = originalLine.trim();

    // Hard fallbacks removed — they were overwriting AI-written session files
    // with scaffold versions, causing infinite fix loops. Session files are
    // used as-is; server-side validation prevents broken code from being saved.

    // Skip already commented out lines
    if (trimmed.startsWith('/*') || trimmed.startsWith('//')) continue;

    const mismatchedClosingTagMatch = errorText.match(
      /Unexpected closing "([^"]+)" tag does not match opening "([^"]+)" tag/i,
    );

    if (mismatchedClosingTagMatch) {
      const openingTag = mismatchedClosingTagMatch[2];
      if (repairNearestVoidTag(lines, lineIdx, openingTag)) {
        anyFixed = true;
        fileMap[filePath] = lines.join('\n');
        continue;
      }

      if (trimmed === `</${mismatchedClosingTagMatch[1]}>`) {
        lines[lineIdx] = '';
        anyFixed = true;
        fileMap[filePath] = lines.join('\n');
        continue;
      }
    }

    if (errorText.includes('Expected identifier but found "/"')) {
      // Stray slash — either at line start or mid-line
      // Try targeted fix: if it looks like a broken closing tag, repair it

      // Pattern 1: /TagName> on same line (missing <)
      const brokenTag = trimmed.match(/^(.*?)\/([A-Za-z][A-Za-z0-9.]*)\s*>(.*)$/);
      if (brokenTag) {
        lines[lineIdx] = `${indent}${brokenTag[1]}</${brokenTag[2]}>${brokenTag[3]}`;
      }
      // Pattern 2: /TagName without > (closing tag split across lines or missing >)
      else if (/^\/([A-Za-z][A-Za-z0-9.]*)/.test(trimmed)) {
        const tagMatch = trimmed.match(/^\/([A-Za-z][A-Za-z0-9.]*)\s*(.*)$/);
        if (tagMatch) {
          const tagName = tagMatch[1];
          const rest = tagMatch[2];
          // Check if next line starts with > (tag split across lines)
          if (lineIdx + 1 < lines.length && lines[lineIdx + 1].trim().startsWith('>')) {
            lines[lineIdx] = `${indent}</${tagName}>`;
            // Remove the orphaned > on the next line
            const nextIndent = lines[lineIdx + 1].match(/^(\s*)/)?.[1] ?? '';
            const nextRest = lines[lineIdx + 1].trim().slice(1).trim();
            lines[lineIdx + 1] = nextRest ? `${nextIndent}${nextRest}` : '';
          } else {
            // No > found — add < prefix and > suffix to make valid closing tag
            lines[lineIdx] = `${indent}</${tagName}>${rest ? ' ' + rest : ''}`;
          }
        } else {
          lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
        }
      }
      // Pattern 3: bare / or /> — comment out
      else if (trimmed === '/' || trimmed === '/>') {
        lines[lineIdx] = `${indent}/* [autofix] ${trimmed} */`;
      }
      // Pattern 4: // comment inside JSX — convert to block comment
      else if (trimmed.startsWith('//')) {
        const commentText = trimmed.slice(2).trim();
        // Use plain JS block comments so the line is valid in both JSX and non-JSX contexts.
        lines[lineIdx] = `${indent}/* ${commentText} */`;
      } else {
        // Generic fallback: comment out the entire line
        lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      }
      anyFixed = true;
    } else if (
      errorText.includes('Expected') &&
      errorText.includes('...') &&
      (errorText.includes('}') || errorText.includes(')'))
    ) {
      // Orphaned spread/destructuring issue. Handle common malformed patterns
      // seen in AI-generated TSX such as dangling commas before closers.
      let fixedLine = originalLine.replace(/\.\.\.\s*\}/g, '}');
      fixedLine = fixedLine.replace(/,\s*\.\.\.\s*([,})\]])/g, ' $1');
      fixedLine = fixedLine.replace(/,\s*}/g, ' }');
      fixedLine = fixedLine.replace(/,\s*\)/g, ' )');
      fixedLine = fixedLine.replace(/\{\s*,/g, '{ ');
      fixedLine = fixedLine.replace(/\(\s*,/g, '(');
      fixedLine = fixedLine.replace(/,\s*]/g, ' ]');
      // Remove empty JSX spread placeholders like "{ }" / "{}" in opening tags.
      fixedLine = fixedLine.replace(/\{\s*\}/g, '');
      // Collapse malformed object/param blocks that only contain commas.
      fixedLine = fixedLine.replace(/\{\s*,+\s*\}/g, '{}');
      fixedLine = fixedLine.replace(/\(\s*,+\s*\)/g, '()');
      // Common shadcn/props corruption: ", }" on prior prop line.
      fixedLine = fixedLine.replace(/,\s*}/g, '}');
      // If the parser points at a lone closing token and previous line has a trailing comma,
      // strip that comma instead of nuking the line.
      if ((trimmed === '}' || trimmed === ')' || trimmed === ']') && lineIdx > 0) {
        lines[lineIdx - 1] = lines[lineIdx - 1].replace(/,\s*$/, '');
      }
      // If this line is just a dangling "}" inside a JSX opening tag, drop it.
      if (trimmed === '}' && lineIdx > 0) {
        const prev = lines[lineIdx - 1].trim();
        const next = lineIdx + 1 < lines.length ? lines[lineIdx + 1].trim() : '';
        if (
          prev.startsWith('<') &&
          !prev.endsWith('>') &&
          (next.startsWith('/>') || next === '>')
        ) {
          lines[lineIdx] = '';
          anyFixed = true;
          fileMap[filePath] = lines.join('\n');
          continue;
        }
      }
      if (fixedLine === originalLine) {
        lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      } else {
        lines[lineIdx] = fixedLine;
      }
      anyFixed = true;
    } else if (
      errorText.includes('The character "}" is not valid inside a JSX element') ||
      errorText.includes("The character '}' is not valid inside a JSX element")
    ) {
      if (/^}\s*,?$/.test(trimmed)) {
        lines[lineIdx] = '';
      } else {
        lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      }
      anyFixed = true;
    } else if (
      errorText.includes('Unexpected "}"') ||
      errorText.includes('Unexpected ")"') ||
      errorText.includes('Unexpected ">"') ||
      errorText.includes('Unexpected "<<"')
    ) {
      if (errorText.includes('Unexpected "<<"')) {
        const fixedLine = originalLine.replace(/(^|\s)<<(?=\s*(?:[A-Za-z!/{]|$))/g, '$1<');
        if (fixedLine !== originalLine) {
          lines[lineIdx] = fixedLine;
          anyFixed = true;
          fileMap[filePath] = lines.join('\n');
          continue;
        }
        // If line-level rewrite misses, sanitize whole file to collapse all doubled open tokens.
        const fullySanitized = content.replace(/(^|\s)<<(?=\s*(?:[A-Za-z!/{]|$))/gm, '$1<');
        if (fullySanitized !== content) {
          fileMap[filePath] = fullySanitized;
          anyFixed = true;
          continue;
        }
      }
      lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      anyFixed = true;
    } else if (errorText.includes('Expected ">" but found ")"')) {
      // Malformed JSX opener where ")" was produced instead of ">".
      // Example:
      //   <Button
      //     className="x"
      //   )
      //   </Button>
      // Fix by converting the offending line to ">" (or "/>" when next line is orphaned "/>").
      const nextTrimmed = lineIdx + 1 < lines.length ? lines[lineIdx + 1].trim() : '';
      if (nextTrimmed === '/>') {
        lines[lineIdx] = `${indent}/>`;
        lines[lineIdx + 1] = '';
        anyFixed = true;
        fileMap[filePath] = lines.join('\n');
        continue;
      }
      if (lineIdx > 0) {
        let prevIdx = lineIdx - 1;
        while (prevIdx >= 0 && !lines[prevIdx].trim()) prevIdx--;
        if (prevIdx >= 0) {
          const prevTrim = lines[prevIdx].trim();
          const looksLikeOpenJsx =
            prevTrim.startsWith('<') &&
            !prevTrim.startsWith('</') &&
            !prevTrim.endsWith('>') &&
            !prevTrim.endsWith('/>');
          if (looksLikeOpenJsx) {
            lines[lineIdx] = `${indent}>`;
            anyFixed = true;
            fileMap[filePath] = lines.join('\n');
            continue;
          }
        }
      }
      lines[lineIdx] = `${indent}>`;
      anyFixed = true;
    } else if (errorText.includes('Expected ">" but found "<"')) {
      // Common malformed JSX pattern:
      // <SomeComponent
      //   ...
      // <Child />
      // The opening tag is missing a closing ">" before the next "<".
      // First, use the parser column to repair in-place by inserting a missing ">"
      // immediately before the offending "<" when possible.
      if (Number.isFinite(colNum) && colNum >= 0) {
        const idxCandidates = [colNum, colNum - 1].filter((n) => n >= 0 && n < originalLine.length);
        let fixedAtColumn = false;
        for (const idx of idxCandidates) {
          if (originalLine[idx] === '<') {
            lines[lineIdx] = `${originalLine.slice(0, idx)}>${originalLine.slice(idx)}`;
            anyFixed = true;
            fileMap[filePath] = lines.join('\n');
            fixedAtColumn = true;
            break;
          }
        }
        if (!fixedAtColumn) {
          const start = Math.max(0, colNum - 2);
          const rel = originalLine.slice(start).indexOf('<');
          if (rel !== -1) {
            const idx = start + rel;
            lines[lineIdx] = `${originalLine.slice(0, idx)}>${originalLine.slice(idx)}`;
            anyFixed = true;
            fileMap[filePath] = lines.join('\n');
            continue;
          }
        } else {
          continue;
        }
      }

      // If a single line contains two tag starts (`<...<...`), close the first
      // opener before the second tag.
      {
        const tagStarts = Array.from(originalLine.matchAll(/</g))
          .map((m) => m.index ?? -1)
          .filter((i) => i >= 0);
        if (tagStarts.length >= 2) {
          const second = tagStarts[1];
          const beforeSecond = second > 0 ? originalLine[second - 1] : '';
          if (beforeSecond !== '>' && beforeSecond !== '/') {
            lines[lineIdx] = `${originalLine.slice(0, second)}>${originalLine.slice(second)}`;
            anyFixed = true;
            fileMap[filePath] = lines.join('\n');
            continue;
          }
        }
      }

      // Also handle inline malformed cases like:
      //   <div className="card" <span>Title</span>
      // by inserting ">" before the nested "<".
      const inlineFixed = originalLine.replace(
        /(<[A-Za-z][A-Za-z0-9.\-]*(?:[^>"'`{]|"[^"]*"|'[^']*'|`[^`]*`|\{[^}]*\})*)\s<(?=[A-Za-z/])/,
        '$1> <',
      );
      if (inlineFixed !== originalLine) {
        lines[lineIdx] = inlineFixed;
        anyFixed = true;
        fileMap[filePath] = lines.join('\n');
        continue;
      }

      if (lineIdx > 0) {
        let prevIdx = lineIdx - 1;
        while (prevIdx >= 0 && !lines[prevIdx].trim()) prevIdx--;
        if (prevIdx >= 0) {
          const prev = lines[prevIdx];
          const prevTrim = prev.trim();
          const looksLikeOpenJsx =
            prevTrim.startsWith('<') &&
            !prevTrim.startsWith('</') &&
            !prevTrim.endsWith('>') &&
            !prevTrim.endsWith('/>');
          if (looksLikeOpenJsx) {
            lines[prevIdx] = `${prev}>`;
            anyFixed = true;
            fileMap[filePath] = lines.join('\n');
            continue;
          }
        }
      }
      // Fallback: neutralize the failing line so build can continue.
      lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      anyFixed = true;
    } else if (
      errorText.includes('Expected "}" but found') ||
      errorText.includes('Expected ")" but found')
    ) {
      // Missing closing token — append at end of file
      const token = errorText.includes('"}"') ? '}' : ')';
      lines.push(token);
      anyFixed = true;
    } else if (/Unexpected end of file before a closing "([^"]+)" tag/i.test(errorText)) {
      const eofClosingTag = errorText.match(/Unexpected end of file before a closing "([^"]+)" tag/i);
      if (eofClosingTag?.[1]) {
        lines.push(`</${eofClosingTag[1]}>`);
        anyFixed = true;
      }
    } else if (errorText.includes('Unterminated') || errorText.includes('unterminated')) {
      // Unterminated string/regex/template — comment out the line
      lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      anyFixed = true;
    } else {
      // Generic fallback: comment out the problematic line
      lines[lineIdx] = `${indent}/* [autofix] ${trimmed.replace(/\*\//g, '* /')} */`;
      anyFixed = true;
    }

    fileMap[filePath] = lines.join('\n');
  }

  return anyFixed;
}

/**
 * Normalize common AI-generation artifacts before parsing.
 * These artifacts are not valid source and frequently trigger virtual-fs parse failures.
 */
function normalizeGeneratedSourceArtifacts(fileMap: Record<string, string>): boolean {
  let changed = false;

  const unwrapSingleFence = (content: string): string => {
    const trimmed = content.trim();
    if (!trimmed.startsWith('```') || !trimmed.endsWith('```')) return content;
    const lines = trimmed.split('\n');
    if (lines.length < 2) return content;
    const first = lines[0];
    const last = lines[lines.length - 1];
    if (!/^```[\w-]*\s*$/.test(first) || !/^```\s*$/.test(last)) return content;
    return lines.slice(1, -1).join('\n');
  };

  const shouldNormalizeCodeFile = (path: string, content: string) => {
    if (!content || content.startsWith('data:')) return false;
    // Skip clearly binary/static assets.
    if (/\.(png|jpe?g|gif|webp|avif|ico|bmp|mp4|webm|ogg|mp3|wav|woff2?|ttf|otf|eot|wasm)$/i.test(path)) {
      return false;
    }
    return true;
  };

  for (const [path, raw] of Object.entries(fileMap)) {
    if (!raw || !shouldNormalizeCodeFile(path, raw)) continue;

    let next = raw;
    next = unwrapSingleFence(next);

    // Strip git merge conflict markers accidentally injected by model output.
    next = next
      .split('\n')
      .filter((line) => !/^\s*(<<<<<<<|=======|>>>>>>>)/.test(line))
      .join('\n');

    if (/\.(tsx|jsx)$/.test(path)) {
      // Fix accidental doubled JSX open token: <<div -> <div
      // Include "{" lookahead because AI output often corrupts JSX expressions as "<<{".
      next = next.replace(/(^|\s)<<(?=\s*(?:[A-Za-z!/{]|$))/g, '$1<');
      // Also normalize line-leading doubled tokens with no preceding whitespace context.
      next = next.replace(/^\s*<<(?=\s*(?:[A-Za-z!/{]|$))/gm, '<');
      // Convert JSX-hostile line comments to block comments.
      next = next.replace(/(^\s*)\/\/([^\n]*)$/gm, '$1/* $2 */');
      // JSX requires void HTML elements like <img> and <input> to be self-closing.
      next = selfCloseVoidJsxTags(next);
    }

    if (next !== raw) {
      fileMap[path] = next;
      changed = true;
    }
  }

  return changed;
}

function formatEsbuildErrorsForAutofix(err: unknown): string {
  const fallback = err instanceof Error ? err.message : String(err);
  const details = (err as any)?.errors;
  if (!Array.isArray(details) || details.length === 0) return fallback;

  const normalized = details
    .map((e: any) => {
      const fileRaw = String(e?.location?.file || '').replace(/\\/g, '/');
      const file = '/' + fileRaw.replace(/^virtual-fs:/i, '').replace(/^\/+/, '');
      const line = Number(e?.location?.line || 0);
      const column = Number(e?.location?.column || 0);
      const text = String(e?.text || e?.message || '').trim();
      if (!file || !text) return '';
      return `virtual-fs:${file}:${line}:${column}: ERROR: ${text}`;
    })
    .filter(Boolean)
    .join('\n');

  return normalized || fallback;
}

function loaderForPath(path: string): 'ts' | 'tsx' | 'js' | 'jsx' | 'css' | 'json' | null {
  if (path.endsWith('.tsx')) return 'tsx';
  if (path.endsWith('.ts')) return 'ts';
  if (path.endsWith('.jsx')) return 'jsx';
  if (path.endsWith('.js')) return 'js';
  if (path.endsWith('.css')) return 'css';
  if (path.endsWith('.json')) return 'json';
  return null;
}

function formatTransformErrorsForAutofix(filePath: string, err: unknown): string {
  const details = (err as any)?.errors;
  if (!Array.isArray(details) || details.length === 0) return '';
  return details
    .map((e: any) => {
      const line = Number(e?.location?.line || 0);
      const column = Number(e?.location?.column || 0);
      const text = String(e?.text || e?.message || '').trim();
      if (!text) return '';
      return `virtual-fs:${filePath}:${line}:${column}: ERROR: ${text}`;
    })
    .filter(Boolean)
    .join('\n');
}

/**
 * Preflight parse check for source files before the full bundle.
 * This catches malformed TSX/JSX early and reuses autoFixBuildErrors with
 * precise locations so the full build is far less likely to fail repeatedly.
 */
async function preflightParseAndRepair(fileMap: Record<string, string>): Promise<boolean> {
  const candidates = Object.keys(fileMap)
    .filter((p) => /\.(tsx|ts|jsx|js)$/.test(p))
    // Prefer primary app entry files first for faster stabilization.
    .sort((a, b) => {
      const score = (p: string) => {
        if (/\/src\/App\.(tsx|jsx)$/.test(p)) return 0;
        if (/\/src\/main\.(tsx|ts|jsx|js)$/.test(p)) return 1;
        if (/\/src\/components\//.test(p)) return 2;
        return 3;
      };
      return score(a) - score(b);
    })
    ; // Process all parseable source files so no page/component is skipped.

  if (candidates.length === 0) return false;

  let anyFixed = false;
  const MAX_PREFLIGHT_PASSES = 2;

  for (let pass = 0; pass < MAX_PREFLIGHT_PASSES; pass++) {
    let passFixed = false;
    for (const filePath of candidates) {
      const content = fileMap[filePath];
      if (!content) continue;
      if (content.length > 500_000) continue; // Avoid very large file parse cost in preflight.
      const loader = loaderForPath(filePath);
      if (!loader || (loader !== 'ts' && loader !== 'tsx' && loader !== 'js' && loader !== 'jsx')) {
        continue;
      }

      try {
        await esbuild.transform(content, {
          loader,
          format: 'esm',
          logLevel: 'silent',
          jsx: 'automatic',
          jsxImportSource: 'react',
        });
      } catch (err) {
        const normalized = formatTransformErrorsForAutofix(filePath, err);
        if (!normalized) continue;
        const fixed = autoFixBuildErrors(fileMap, normalized);
        if (fixed) {
          passFixed = true;
          anyFixed = true;
        }
      }
    }
    if (!passFixed) break;
  }

  return anyFixed;
}

/**
 * Parse-check fragile boilerplate files before full bundle.
 * If syntactically invalid, replace with known-good bundled shadcn implementations.
 */
async function stabilizeCriticalPreviewFiles(fileMap: Record<string, string>): Promise<boolean> {
  const bundled = getShadcnBundledFiles();
  const critical: Array<{
    name: string;
    paths: string[];
    fallback: string;
    loader: 'tsx' | 'ts' | 'jsx' | 'js';
  }> = [
    {
      name: 'theme-provider',
      paths: ['/src/components/theme-provider.tsx', '/components/theme-provider.tsx'],
      fallback: SHADCN_THEME_PROVIDER,
      loader: 'tsx',
    },
    {
      name: 'ui/button',
      paths: ['/src/components/ui/button.tsx', '/components/ui/button.tsx'],
      fallback: bundled['src/components/ui/button.tsx'] || '',
      loader: 'tsx',
    },
    {
      name: 'ui/card',
      paths: ['/src/components/ui/card.tsx', '/components/ui/card.tsx'],
      fallback: bundled['src/components/ui/card.tsx'] || '',
      loader: 'tsx',
    },
    {
      name: 'ui/badge',
      paths: ['/src/components/ui/badge.tsx', '/components/ui/badge.tsx'],
      fallback: bundled['src/components/ui/badge.tsx'] || '',
      loader: 'tsx',
    },
    {
      name: 'ui/input',
      paths: ['/src/components/ui/input.tsx', '/components/ui/input.tsx'],
      fallback: bundled['src/components/ui/input.tsx'] || SHADCN_INPUT,
      loader: 'tsx',
    },
    {
      name: 'Sidebar',
      paths: ['/src/components/Sidebar.tsx', '/components/Sidebar.tsx'],
      fallback: bundled['src/components/Sidebar.tsx'] || SHADCN_SIDEBAR,
      loader: 'tsx',
    },
  ];

  let replaced = false;
  for (const file of critical) {
    for (const path of file.paths) {
      const content = fileMap[path];
      if (!content || !file.fallback) continue;
      try {
        await esbuild.transform(content, {
          loader: file.loader,
          format: 'esm',
          logLevel: 'silent',
          jsx: 'automatic',
          jsxImportSource: 'react',
        });
      } catch {
        fileMap[path] = file.fallback;
        replaced = true;
        console.warn(`[bundler] Replaced broken ${file.name} with bundled fallback: ${path}`);
      }
    }
  }
  return replaced;
}

// ── Main bundle function ──
export async function bundleProject(options: BundleOptions): Promise<BundleResult> {
  await initEsbuild();

  const fileMap = sessionFilesToPathMap(options.files);
  const hasShadcnUiFiles = Object.keys(fileMap).some((p) =>
    /\/components\/ui\/(button|card|badge|input)\.tsx$/i.test(p),
  );
  const enableShadcnFallbacks =
    ENABLE_PREVIEW_SOURCE_FALLBACKS &&
    (hasShadcnUiFiles ||
      options.framework === 'react' ||
      options.framework === 'unknown' ||
      !options.framework);

  // Normalize malformed generated artifacts before any parser runs.
  if (ENABLE_PREVIEW_SOURCE_FALLBACKS) {
    try {
      const normalized = normalizeGeneratedSourceArtifacts(fileMap);
      if (normalized) {
        console.debug('[bundler] Normalized malformed generated source artifacts');
      }
    } catch (normalizeErr) {
      console.debug('[bundler] Source normalization failed (non-fatal):', normalizeErr);
    }
  }
  if (enableShadcnFallbacks) {
    // Inject known-good shadcn fallback files only for React-like frameworks.
    const themeProviderPaths = [
      '/src/components/theme-provider.tsx',
      '/components/theme-provider.tsx',
    ];
    for (const p of themeProviderPaths) {
      fileMap[p] = SHADCN_THEME_PROVIDER;
    }
    const forcedShadcnPaths = [
      '/src/components/ui/button.tsx',
      '/components/ui/button.tsx',
      '/src/components/ui/card.tsx',
      '/components/ui/card.tsx',
      '/src/components/ui/badge.tsx',
      '/components/ui/badge.tsx',
      '/src/components/ui/input.tsx',
      '/components/ui/input.tsx',
    ] as const;
    const forcedShadcnSource: Record<string, string> = {
      '/src/components/ui/button.tsx': getShadcnBundledFiles()['src/components/ui/button.tsx'] || '',
      '/components/ui/button.tsx': getShadcnBundledFiles()['src/components/ui/button.tsx'] || '',
      '/src/components/ui/card.tsx': getShadcnBundledFiles()['src/components/ui/card.tsx'] || '',
      '/components/ui/card.tsx': getShadcnBundledFiles()['src/components/ui/card.tsx'] || '',
      '/src/components/ui/badge.tsx': getShadcnBundledFiles()['src/components/ui/badge.tsx'] || '',
      '/components/ui/badge.tsx': getShadcnBundledFiles()['src/components/ui/badge.tsx'] || '',
      '/src/components/ui/input.tsx': getShadcnBundledFiles()['src/components/ui/input.tsx'] || '',
      '/components/ui/input.tsx': getShadcnBundledFiles()['src/components/ui/input.tsx'] || '',
    };
    for (const p of forcedShadcnPaths) {
      const fallback = forcedShadcnSource[p];
      if (fallback) fileMap[p] = fallback;
    }

    // Inject bundled shadcn/ui components as fallbacks when session files don't exist.
    const bundledFiles = getShadcnBundledFiles();
    for (const [relPath, content] of Object.entries(bundledFiles)) {
      const absPath = '/' + relPath;
      if (fileMap[absPath] === undefined) {
        fileMap[absPath] = content;
      }
    }
  }

  // Strip BrowserRouter wrapping from main.tsx when App.tsx already has its own Router.
  // AI-generated code often wraps <App /> in <BrowserRouter> in BOTH main.tsx AND App.tsx,
  // causing duplicate Router contexts. Only strip from main.tsx if App.tsx already has a
  // Router — otherwise Routes/Route inside App.tsx would crash.
  {
    const mainKey = fileMap['/src/main.tsx']
      ? '/src/main.tsx'
      : fileMap['/src/main.ts']
        ? '/src/main.ts'
        : null;
    const appSrc = fileMap['/src/App.tsx'] || fileMap['/src/App.jsx'] || '';
    const appHasRouter = /<\s*(?:BrowserRouter|MemoryRouter|HashRouter|RouterProvider)\b/.test(
      appSrc,
    );
    if (mainKey && appHasRouter) {
      let mainSrc = fileMap[mainKey];
      if (/<\s*BrowserRouter/.test(mainSrc)) {
        // Remove <BrowserRouter> and </BrowserRouter> tags wrapping <App />
        mainSrc = mainSrc.replace(/<\s*BrowserRouter\s*>\s*/g, '');
        mainSrc = mainSrc.replace(/\s*<\/\s*BrowserRouter\s*>/g, '');
        // Remove BrowserRouter from the import if no longer referenced in JSX
        if (!/BrowserRouter/.test(mainSrc.replace(/import[^;]*BrowserRouter[^;]*/g, ''))) {
          mainSrc = mainSrc.replace(
            /import\s*\{([^}]*)BrowserRouter\s*,?\s*([^}]*)\}\s*from\s*['"]react-router-dom['"]\s*;?/g,
            (_full: string, before: string, after: string) => {
              const remaining = [before, after]
                .join('')
                .replace(/,\s*,/g, ',')
                .replace(/^\s*,|,\s*$/g, '')
                .trim();
              if (!remaining) return '';
              return `import { ${remaining} } from 'react-router-dom';`;
            },
          );
        }
        fileMap[mainKey] = mainSrc;
      }
    }
  }

  // Ensure index.css exists when main.tsx imports it — AI code generation sometimes
  // drops the CSS file while keeping the import in main.tsx.
  // Use shadcn CSS variables (design tokens like --background, --primary, etc.)
  // so shadcn components get their CSS custom properties.
  const mainEntryKey = fileMap['/src/main.tsx']
    ? '/src/main.tsx'
    : fileMap['/src/main.ts']
      ? '/src/main.ts'
      : null;
  const mainEntry = mainEntryKey ? fileMap[mainEntryKey] : '';
  if (mainEntry.includes('./index.css') && fileMap['/src/index.css'] === undefined) {
    fileMap['/src/index.css'] = getShadcnCssVariables();
  }

  // Detect whether the session uses shadcn components (by path or import reference).
  const usesShadcn =
    Object.keys(fileMap).some((p) => p.includes('/components/ui/')) ||
    Object.values(fileMap).some((content) => content.includes('@/components/ui/'));

  // If main.tsx exists but doesn't import any CSS file, AND the session uses shadcn
  // components or Tailwind utility classes, inject the CSS import + create the file.
  // This handles AI-generated code that forgets `import './index.css'` in main.tsx,
  // or puts CSS at a non-standard path (e.g. src/styles/globals.css) without importing it.
  if (mainEntryKey && mainEntry && !mainEntry.includes('.css')) {
    const hasTwClasses = Object.values(fileMap).some(
      (content) =>
        /className[=:]\s*["'][^"']*\b(?:bg|text|border)-\w+-\d{2,3}\b/.test(content) ||
        /className[=:]\s*["'][^"']*\b(?:flex|grid|justify|items)-/.test(content),
    );
    if (usesShadcn || hasTwClasses) {
      // Find an existing CSS file in the session (AI may have generated one at a custom path).
      // Prefer well-known names: globals.css > index.css > app.css > any .css under /src
      const PREFERRED = ['globals.css', 'index.css', 'app.css', 'styles.css', 'main.css'];
      const allCssFiles = Object.keys(fileMap).filter((p) => p.endsWith('.css'));
      let existingCss =
        allCssFiles.find((p) =>
          PREFERRED.some((name) => p === `/src/${name}` || p === `/src/styles/${name}`),
        ) ??
        allCssFiles.find((p) => p.startsWith('/src/')) ??
        null;

      let importPath: string;
      if (existingCss) {
        // Build a relative import path from main.tsx's directory to the CSS file
        const mainDir = mainEntryKey.substring(0, mainEntryKey.lastIndexOf('/'));
        if (existingCss.startsWith(mainDir + '/')) {
          importPath = './' + existingCss.slice(mainDir.length + 1);
        } else {
          // Walk up to common ancestor (handles /src/styles/globals.css from /src/main.tsx)
          const fromParts = mainDir.split('/').filter(Boolean);
          const toParts = existingCss.split('/').filter(Boolean);
          let common = 0;
          while (
            common < fromParts.length &&
            common < toParts.length &&
            fromParts[common] === toParts[common]
          )
            common++;
          importPath = '../'.repeat(fromParts.length - common) + toParts.slice(common).join('/');
          if (!importPath.startsWith('.')) importPath = './' + importPath;
        }
        // Ensure the existing CSS file has the shadcn/Tailwind foundation.
        // AI-generated globals.css often has @theme tokens or :root variables but omits
        // `@import "tailwindcss"` and the color→variable bridge mappings. Without them
        // hasTailwind stays false and bg-background/text-foreground produce nothing.
        const existingCssContent = fileMap[existingCss] || '';
        if (
          !existingCssContent.includes('@import "tailwindcss"') &&
          !existingCssContent.includes("@import 'tailwindcss'")
        ) {
          fileMap[existingCss] = getShadcnCssVariables() + '\n' + existingCssContent;
        }
      } else {
        // No CSS file in session — create the standard one
        importPath = './index.css';
        fileMap['/src/index.css'] = getShadcnCssVariables();
      }
      fileMap[mainEntryKey] = `import '${importPath}';\n` + mainEntry;
    }
  }

  // If index.css exists but has no shadcn CSS variables and the session uses shadcn
  // components, prepend the variables so theme tokens are defined.
  const existingIndexCss = fileMap['/src/index.css'] || '';
  if (usesShadcn && existingIndexCss && !existingIndexCss.includes('--background')) {
    fileMap['/src/index.css'] = getShadcnCssVariables() + '\n' + existingIndexCss;
  }

  // SvelteKit synthetic entry: if the project has +page.svelte or +layout.svelte
  // but no explicit main.ts/js entry, inject a synthetic entry that mounts the component.
  injectSvelteKitEntry(fileMap);

  // React synthetic entry: if App.tsx exists but no main.tsx imports it,
  // inject a synthetic main.tsx that mounts the App component with createRoot.
  injectReactEntry(fileMap);

  // Detect Tailwind BEFORE stripping directives — the stripping removes @import "tailwindcss"
  // and @theme blocks that detectHasTailwind relies on for CSS-based detection.
  const hasTailwind = detectHasTailwind(fileMap);
  console.debug('[bundler] hasTailwind:', hasTailwind, '| usesShadcn:', usesShadcn);

  // ── @apply inline expansion map ──
  // Maps common Tailwind utility names → CSS declarations so @apply can be resolved
  // at bundle time instead of being commented out (which breaks custom classes).
  const TW: Record<string, string> = {
    // Display
    flex: 'display: flex;', 'inline-flex': 'display: inline-flex;', grid: 'display: grid;',
    hidden: 'display: none;', block: 'display: block;', 'inline-block': 'display: inline-block;',
    inline: 'display: inline;', contents: 'display: contents;',
    // Position
    relative: 'position: relative;', absolute: 'position: absolute;',
    fixed: 'position: fixed;', sticky: 'position: sticky;', static: 'position: static;',
    'inset-0': 'inset: 0;', 'top-0': 'top: 0;', 'right-0': 'right: 0;',
    'bottom-0': 'bottom: 0;', 'left-0': 'left: 0;',
    // Flex / Grid alignment
    'items-center': 'align-items: center;', 'items-start': 'align-items: flex-start;',
    'items-end': 'align-items: flex-end;', 'items-stretch': 'align-items: stretch;',
    'items-baseline': 'align-items: baseline;',
    'justify-center': 'justify-content: center;', 'justify-between': 'justify-content: space-between;',
    'justify-start': 'justify-content: flex-start;', 'justify-end': 'justify-content: flex-end;',
    'justify-around': 'justify-content: space-around;', 'justify-evenly': 'justify-content: space-evenly;',
    'self-center': 'align-self: center;', 'self-start': 'align-self: flex-start;',
    'self-end': 'align-self: flex-end;', 'self-auto': 'align-self: auto;',
    'content-center': 'align-content: center;',
    // Flex direction / wrap
    'flex-col': 'flex-direction: column;', 'flex-row': 'flex-direction: row;',
    'flex-col-reverse': 'flex-direction: column-reverse;', 'flex-row-reverse': 'flex-direction: row-reverse;',
    'flex-wrap': 'flex-wrap: wrap;', 'flex-nowrap': 'flex-wrap: nowrap;',
    'flex-1': 'flex: 1 1 0%;', 'flex-auto': 'flex: 1 1 auto;', 'flex-none': 'flex: none;',
    'flex-initial': 'flex: 0 1 auto;', grow: 'flex-grow: 1;', 'grow-0': 'flex-grow: 0;',
    shrink: 'flex-shrink: 1;', 'shrink-0': 'flex-shrink: 0;',
    // Gap
    'gap-0': 'gap: 0;', 'gap-0.5': 'gap: 0.125rem;', 'gap-1': 'gap: 0.25rem;',
    'gap-1.5': 'gap: 0.375rem;', 'gap-2': 'gap: 0.5rem;', 'gap-2.5': 'gap: 0.625rem;',
    'gap-3': 'gap: 0.75rem;', 'gap-4': 'gap: 1rem;', 'gap-5': 'gap: 1.25rem;',
    'gap-6': 'gap: 1.5rem;', 'gap-8': 'gap: 2rem;', 'gap-10': 'gap: 2.5rem;',
    'gap-12': 'gap: 3rem;', 'gap-16': 'gap: 4rem;',
    'gap-x-1': 'column-gap: 0.25rem;', 'gap-x-2': 'column-gap: 0.5rem;',
    'gap-x-3': 'column-gap: 0.75rem;', 'gap-x-4': 'column-gap: 1rem;',
    'gap-y-1': 'row-gap: 0.25rem;', 'gap-y-2': 'row-gap: 0.5rem;',
    'gap-y-3': 'row-gap: 0.75rem;', 'gap-y-4': 'row-gap: 1rem;',
    // Padding
    'p-0': 'padding: 0;', 'p-1': 'padding: 0.25rem;', 'p-2': 'padding: 0.5rem;',
    'p-3': 'padding: 0.75rem;', 'p-4': 'padding: 1rem;', 'p-5': 'padding: 1.25rem;',
    'p-6': 'padding: 1.5rem;', 'p-8': 'padding: 2rem;', 'p-10': 'padding: 2.5rem;',
    'p-12': 'padding: 3rem;', 'p-16': 'padding: 4rem;',
    'px-0': 'padding-left: 0; padding-right: 0;',
    'px-1': 'padding-left: 0.25rem; padding-right: 0.25rem;',
    'px-2': 'padding-left: 0.5rem; padding-right: 0.5rem;',
    'px-3': 'padding-left: 0.75rem; padding-right: 0.75rem;',
    'px-4': 'padding-left: 1rem; padding-right: 1rem;',
    'px-5': 'padding-left: 1.25rem; padding-right: 1.25rem;',
    'px-6': 'padding-left: 1.5rem; padding-right: 1.5rem;',
    'px-8': 'padding-left: 2rem; padding-right: 2rem;',
    'py-0': 'padding-top: 0; padding-bottom: 0;',
    'py-1': 'padding-top: 0.25rem; padding-bottom: 0.25rem;',
    'py-2': 'padding-top: 0.5rem; padding-bottom: 0.5rem;',
    'py-3': 'padding-top: 0.75rem; padding-bottom: 0.75rem;',
    'py-4': 'padding-top: 1rem; padding-bottom: 1rem;',
    'py-6': 'padding-top: 1.5rem; padding-bottom: 1.5rem;',
    'py-8': 'padding-top: 2rem; padding-bottom: 2rem;',
    'pt-0': 'padding-top: 0;', 'pt-2': 'padding-top: 0.5rem;', 'pt-4': 'padding-top: 1rem;',
    'pb-0': 'padding-bottom: 0;', 'pb-2': 'padding-bottom: 0.5rem;', 'pb-4': 'padding-bottom: 1rem;',
    'pl-0': 'padding-left: 0;', 'pl-4': 'padding-left: 1rem;',
    'pr-0': 'padding-right: 0;', 'pr-4': 'padding-right: 1rem;',
    // Margin
    'm-0': 'margin: 0;', 'm-auto': 'margin: auto;', 'm-1': 'margin: 0.25rem;',
    'm-2': 'margin: 0.5rem;', 'm-4': 'margin: 1rem;',
    'mx-auto': 'margin-left: auto; margin-right: auto;',
    'mx-0': 'margin-left: 0; margin-right: 0;',
    'mx-4': 'margin-left: 1rem; margin-right: 1rem;',
    'my-0': 'margin-top: 0; margin-bottom: 0;',
    'my-2': 'margin-top: 0.5rem; margin-bottom: 0.5rem;',
    'my-4': 'margin-top: 1rem; margin-bottom: 1rem;',
    'mt-0': 'margin-top: 0;', 'mt-1': 'margin-top: 0.25rem;', 'mt-2': 'margin-top: 0.5rem;',
    'mt-4': 'margin-top: 1rem;', 'mt-6': 'margin-top: 1.5rem;', 'mt-8': 'margin-top: 2rem;',
    'mt-auto': 'margin-top: auto;',
    'mb-0': 'margin-bottom: 0;', 'mb-1': 'margin-bottom: 0.25rem;', 'mb-2': 'margin-bottom: 0.5rem;',
    'mb-4': 'margin-bottom: 1rem;', 'mb-6': 'margin-bottom: 1.5rem;', 'mb-8': 'margin-bottom: 2rem;',
    'mb-auto': 'margin-bottom: auto;',
    'ml-0': 'margin-left: 0;', 'ml-auto': 'margin-left: auto;', 'ml-2': 'margin-left: 0.5rem;',
    'mr-0': 'margin-right: 0;', 'mr-auto': 'margin-right: auto;', 'mr-2': 'margin-right: 0.5rem;',
    // Width / Height
    'w-full': 'width: 100%;', 'w-auto': 'width: auto;', 'w-screen': 'width: 100vw;',
    'w-0': 'width: 0;', 'w-1/2': 'width: 50%;', 'w-1/3': 'width: 33.333%;',
    'w-2/3': 'width: 66.667%;', 'w-1/4': 'width: 25%;', 'w-3/4': 'width: 75%;',
    'w-fit': 'width: fit-content;', 'w-max': 'width: max-content;', 'w-min': 'width: min-content;',
    'h-full': 'height: 100%;', 'h-auto': 'height: auto;', 'h-screen': 'height: 100vh;',
    'h-0': 'height: 0;', 'h-fit': 'height: fit-content;',
    'min-h-0': 'min-height: 0;', 'min-h-full': 'min-height: 100%;',
    'min-h-screen': 'min-height: 100vh;', 'min-h-fit': 'min-height: fit-content;',
    'min-w-0': 'min-width: 0;', 'min-w-full': 'min-width: 100%;',
    'max-w-none': 'max-width: none;', 'max-w-full': 'max-width: 100%;',
    'max-w-screen-sm': 'max-width: 640px;', 'max-w-screen-md': 'max-width: 768px;',
    'max-w-screen-lg': 'max-width: 1024px;', 'max-w-screen-xl': 'max-width: 1280px;',
    'max-w-xs': 'max-width: 20rem;', 'max-w-sm': 'max-width: 24rem;',
    'max-w-md': 'max-width: 28rem;', 'max-w-lg': 'max-width: 32rem;',
    'max-w-xl': 'max-width: 36rem;', 'max-w-2xl': 'max-width: 42rem;',
    'max-w-3xl': 'max-width: 48rem;', 'max-w-4xl': 'max-width: 56rem;',
    'max-w-5xl': 'max-width: 64rem;', 'max-w-6xl': 'max-width: 72rem;',
    'max-w-7xl': 'max-width: 80rem;',
    'max-h-full': 'max-height: 100%;', 'max-h-screen': 'max-height: 100vh;',
    // Overflow
    'overflow-hidden': 'overflow: hidden;', 'overflow-auto': 'overflow: auto;',
    'overflow-scroll': 'overflow: scroll;', 'overflow-visible': 'overflow: visible;',
    'overflow-x-auto': 'overflow-x: auto;', 'overflow-x-hidden': 'overflow-x: hidden;',
    'overflow-y-auto': 'overflow-y: auto;', 'overflow-y-hidden': 'overflow-y: hidden;',
    // Border radius
    rounded: 'border-radius: 0.25rem;', 'rounded-sm': 'border-radius: 0.125rem;',
    'rounded-md': 'border-radius: 0.375rem;', 'rounded-lg': 'border-radius: 0.5rem;',
    'rounded-xl': 'border-radius: 0.75rem;', 'rounded-2xl': 'border-radius: 1rem;',
    'rounded-3xl': 'border-radius: 1.5rem;', 'rounded-full': 'border-radius: 9999px;',
    'rounded-none': 'border-radius: 0;',
    // Border
    border: 'border-width: 1px;', 'border-0': 'border-width: 0;', 'border-2': 'border-width: 2px;',
    'border-t': 'border-top-width: 1px;', 'border-b': 'border-bottom-width: 1px;',
    'border-l': 'border-left-width: 1px;', 'border-r': 'border-right-width: 1px;',
    'border-none': 'border-style: none;', 'border-solid': 'border-style: solid;',
    'border-dashed': 'border-style: dashed;',
    // Text
    'text-left': 'text-align: left;', 'text-center': 'text-align: center;',
    'text-right': 'text-align: right;', 'text-justify': 'text-align: justify;',
    'text-xs': 'font-size: 0.75rem; line-height: 1rem;',
    'text-sm': 'font-size: 0.875rem; line-height: 1.25rem;',
    'text-base': 'font-size: 1rem; line-height: 1.5rem;',
    'text-lg': 'font-size: 1.125rem; line-height: 1.75rem;',
    'text-xl': 'font-size: 1.25rem; line-height: 1.75rem;',
    'text-2xl': 'font-size: 1.5rem; line-height: 2rem;',
    'text-3xl': 'font-size: 1.875rem; line-height: 2.25rem;',
    'text-4xl': 'font-size: 2.25rem; line-height: 2.5rem;',
    'text-5xl': 'font-size: 3rem; line-height: 1;',
    'text-6xl': 'font-size: 3.75rem; line-height: 1;',
    'font-thin': 'font-weight: 100;', 'font-light': 'font-weight: 300;',
    'font-normal': 'font-weight: 400;', 'font-medium': 'font-weight: 500;',
    'font-semibold': 'font-weight: 600;', 'font-bold': 'font-weight: 700;',
    'font-extrabold': 'font-weight: 800;', 'font-black': 'font-weight: 900;',
    italic: 'font-style: italic;', 'not-italic': 'font-style: normal;',
    uppercase: 'text-transform: uppercase;', lowercase: 'text-transform: lowercase;',
    capitalize: 'text-transform: capitalize;', 'normal-case': 'text-transform: none;',
    underline: 'text-decoration-line: underline;', 'line-through': 'text-decoration-line: line-through;',
    'no-underline': 'text-decoration-line: none;',
    'leading-none': 'line-height: 1;', 'leading-tight': 'line-height: 1.25;',
    'leading-snug': 'line-height: 1.375;', 'leading-normal': 'line-height: 1.5;',
    'leading-relaxed': 'line-height: 1.625;', 'leading-loose': 'line-height: 2;',
    truncate: 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;',
    'whitespace-nowrap': 'white-space: nowrap;', 'whitespace-normal': 'white-space: normal;',
    'whitespace-pre': 'white-space: pre;',
    // Colors (CSS variable based — matches TAILWIND_DESIGN_BASELINE)
    'bg-background': 'background-color: var(--background);',
    'bg-foreground': 'background-color: var(--foreground);',
    'bg-card': 'background-color: var(--card);',
    'bg-primary': 'background-color: var(--primary);',
    'bg-secondary': 'background-color: var(--secondary);',
    'bg-muted': 'background-color: var(--muted);',
    'bg-accent': 'background-color: var(--accent);',
    'bg-destructive': 'background-color: var(--destructive);',
    'bg-transparent': 'background-color: transparent;',
    'bg-white': 'background-color: #fff;', 'bg-black': 'background-color: #000;',
    'text-foreground': 'color: var(--foreground);',
    'text-primary': 'color: var(--primary);',
    'text-primary-foreground': 'color: var(--primary-foreground);',
    'text-secondary-foreground': 'color: var(--secondary-foreground);',
    'text-muted-foreground': 'color: var(--muted-foreground);',
    'text-accent-foreground': 'color: var(--accent-foreground);',
    'text-destructive': 'color: var(--destructive);',
    'text-white': 'color: #fff;', 'text-black': 'color: #000;',
    'border-border': 'border-color: var(--border);',
    'border-input': 'border-color: var(--input);',
    'border-primary': 'border-color: var(--primary);',
    'border-transparent': 'border-color: transparent;',
    // Opacity
    'opacity-0': 'opacity: 0;', 'opacity-50': 'opacity: 0.5;', 'opacity-100': 'opacity: 1;',
    // Visibility
    visible: 'visibility: visible;', invisible: 'visibility: hidden;',
    // Cursor
    'cursor-pointer': 'cursor: pointer;', 'cursor-default': 'cursor: default;',
    'cursor-not-allowed': 'cursor: not-allowed;',
    // Pointer events
    'pointer-events-none': 'pointer-events: none;', 'pointer-events-auto': 'pointer-events: auto;',
    // Transition
    'transition-all': 'transition-property: all; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms;',
    transition: 'transition-property: color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms;',
    'transition-colors': 'transition-property: color,background-color,border-color,text-decoration-color,fill,stroke; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms;',
    'transition-opacity': 'transition-property: opacity; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms;',
    'transition-transform': 'transition-property: transform; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms;',
    'duration-150': 'transition-duration: 150ms;', 'duration-200': 'transition-duration: 200ms;',
    'duration-300': 'transition-duration: 300ms;', 'duration-500': 'transition-duration: 500ms;',
    'ease-in-out': 'transition-timing-function: cubic-bezier(0.4,0,0.2,1);',
    // Shadow
    'shadow-sm': 'box-shadow: 0 1px 2px 0 rgb(0 0 0/0.05);',
    shadow: 'box-shadow: 0 1px 3px 0 rgb(0 0 0/0.1),0 1px 2px -1px rgb(0 0 0/0.1);',
    'shadow-md': 'box-shadow: 0 4px 6px -1px rgb(0 0 0/0.1),0 2px 4px -2px rgb(0 0 0/0.1);',
    'shadow-lg': 'box-shadow: 0 10px 15px -3px rgb(0 0 0/0.1),0 4px 6px -2px rgb(0 0 0/0.1);',
    'shadow-none': 'box-shadow: none;',
    // Z-index
    'z-0': 'z-index: 0;', 'z-10': 'z-index: 10;', 'z-20': 'z-index: 20;',
    'z-30': 'z-index: 30;', 'z-40': 'z-index: 40;', 'z-50': 'z-index: 50;',
    // Transform
    'transform-none': 'transform: none;',
    // Object fit
    'object-cover': 'object-fit: cover;', 'object-contain': 'object-fit: contain;',
    'object-fill': 'object-fit: fill;', 'object-none': 'object-fit: none;',
    // Grid
    'grid-cols-1': 'grid-template-columns: repeat(1,minmax(0,1fr));',
    'grid-cols-2': 'grid-template-columns: repeat(2,minmax(0,1fr));',
    'grid-cols-3': 'grid-template-columns: repeat(3,minmax(0,1fr));',
    'grid-cols-4': 'grid-template-columns: repeat(4,minmax(0,1fr));',
    'grid-cols-6': 'grid-template-columns: repeat(6,minmax(0,1fr));',
    'grid-cols-12': 'grid-template-columns: repeat(12,minmax(0,1fr));',
    'col-span-1': 'grid-column: span 1 / span 1;', 'col-span-2': 'grid-column: span 2 / span 2;',
    'col-span-3': 'grid-column: span 3 / span 3;', 'col-span-4': 'grid-column: span 4 / span 4;',
    'col-span-6': 'grid-column: span 6 / span 6;', 'col-span-full': 'grid-column: 1 / -1;',
    // Space between (uses margin on children — best effort with gap)
    'space-x-1': 'column-gap: 0.25rem;', 'space-x-2': 'column-gap: 0.5rem;',
    'space-x-3': 'column-gap: 0.75rem;', 'space-x-4': 'column-gap: 1rem;',
    'space-y-1': 'row-gap: 0.25rem;', 'space-y-2': 'row-gap: 0.5rem;',
    'space-y-3': 'row-gap: 0.75rem;', 'space-y-4': 'row-gap: 1rem;',
    'space-y-6': 'row-gap: 1.5rem;', 'space-y-8': 'row-gap: 2rem;',
    // Ring
    'ring-0': 'box-shadow: var(--tw-ring-inset) 0 0 0 0px var(--tw-ring-color,var(--ring));',
    // SR only
    'sr-only': 'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border-width: 0;',
    // Aspect ratio
    'aspect-square': 'aspect-ratio: 1/1;', 'aspect-video': 'aspect-ratio: 16/9;',
    'aspect-auto': 'aspect-ratio: auto;',
    // Container
    container: 'width: 100%; margin-left: auto; margin-right: auto;',
    // Scroll
    'scroll-smooth': 'scroll-behavior: smooth;',
    // Select
    'select-none': 'user-select: none;', 'select-all': 'user-select: all;',
    // Appearance
    'appearance-none': 'appearance: none;',
    // Outline
    'outline-none': 'outline: 2px solid transparent; outline-offset: 2px;',
    // List
    'list-none': 'list-style-type: none;', 'list-disc': 'list-style-type: disc;',
    'list-decimal': 'list-style-type: decimal;',
  };

  /** Expand @apply classes to inline CSS. Unknown classes are silently dropped. */
  function expandApplyClasses(classStr: string): string {
    const classes = classStr.trim().split(/\s+/);
    const rules: string[] = [];
    for (const cls of classes) {
      // Strip responsive/state prefixes (e.g. md:flex → flex, hover:opacity-100 → opacity-100)
      const base = cls.replace(/^(?:sm|md|lg|xl|2xl|hover|focus|active|disabled|dark|group-hover):/, '');
      if (TW[base]) rules.push(TW[base]);
    }
    return rules.length > 0 ? rules.join(' ') : `/* unsupported @apply ${classStr} */`;
  }

  // Strip Tailwind v4 directives from CSS files before esbuild.
  // esbuild doesn't understand @import "tailwindcss", @theme, @custom-variant, @apply, @layer, @plugin, @source.
  // These are processed by @tailwindcss/browser@4 CDN in the preview iframe, not by esbuild.
  for (const [path, content] of Object.entries(fileMap)) {
    if (!path.endsWith('.css')) continue;
    let css = content;
    // Strip @import "tailwindcss" (with any variant like "tailwindcss/preflight")
    css = css.replace(/@import\s+["']tailwindcss[^"']*["'][^;]*;?/g, '');
    // Strip Tailwind v3 directives (@tailwind base/components/utilities)
    css = css.replace(/@tailwind\s+\w+\s*;?/g, '');
    // Strip @plugin and @source directives
    css = css.replace(/@plugin\s+["'][^"']*["'][^;]*;?/g, '');
    css = css.replace(/@source\s+["'][^"']*["'][^;]*;?/g, '');
    // Strip @custom-variant lines
    css = css.replace(/@custom-variant\s+[^;{]+(?:;|\{[^}]*\})/g, '');
    // Strip @theme { ... } and @theme inline { ... } blocks
    css = css.replace(/@theme\s+inline\s*\{[^}]*\}/gs, '');
    css = css.replace(/@theme\s*\{[^}]*\}/gs, '');
    // Expand @apply directives inline — esbuild errors on @apply, so we resolve
    // known Tailwind utilities to their CSS equivalents. Unknown classes are dropped.
    css = css.replace(/@apply\s+([^;]+);/g, (_: string, classes: string) => expandApplyClasses(classes));
    // Unwrap @layer blocks — remove the @layer wrapper but keep inner rules.
    // Uses brace counting to find the matching closing brace.
    css = unwrapAtLayer(css);
    if (css !== content) {
      fileMap[path] = css;
    }
  }

  // Rewrite /_api/imgproxy?url=X to direct URLs in all source files.
  // The esbuild srcdoc preview has no server, so /_api/imgproxy just 404s.
  // Replace with the direct external URL (COEP doesn't apply in srcdoc iframes).
  for (const [path, content] of Object.entries(fileMap)) {
    if (!/\.(tsx?|jsx?|css|html|svelte|vue)$/.test(path)) continue;
    const rewritten = content.replace(
      /\/_api\/imgproxy\?url=([^"'\s`),}]+)/g,
      (_: string, encoded: string) => {
        try {
          let url = decodeURIComponent(encoded);
          // Unwrap double-proxied: if decoded URL is another imgproxy, keep unwrapping
          for (let i = 0; i < 5 && /^\/?_api\/imgproxy\?url=/.test(url); i++) {
            const inner = new URL(url.startsWith('/') ? `http://localhost${url}` : url, 'http://localhost').searchParams.get('url');
            if (inner) url = decodeURIComponent(inner);
            else break;
          }
          return url;
        } catch {
          return `/_api/imgproxy?url=${encoded}`;
        }
      },
    );
    if (rewritten !== content) {
      fileMap[path] = rewritten;
    }
  }

  // Preflight parse/repair before the main bundle to reduce repeated fatal loops
  // from malformed AI-generated TSX/JSX.
  try {
    const stabilizedCritical = enableShadcnFallbacks
      ? await stabilizeCriticalPreviewFiles(fileMap)
      : false;
    if (stabilizedCritical) {
      console.debug('[bundler] Stabilized critical preview files before preflight');
    }
    const preflightFixed = ENABLE_PREVIEW_SOURCE_FALLBACKS
      ? await preflightParseAndRepair(fileMap)
      : false;
    if (preflightFixed) {
      console.debug('[bundler] Preflight parse repair applied before bundle');
    }
  } catch (preflightErr) {
    // Non-fatal: continue to the normal build pipeline.
    console.debug('[bundler] Preflight parse check failed (non-fatal):', preflightErr);
  }

  const entryPoint = detectEntryPoint(fileMap);
  if (!entryPoint) {
    return {
      html: '',
      errors: [
        {
          text: 'No entry point found. Expected one of: /src/main.tsx, /src/App.tsx, /src/App.jsx, or an index.html with a script tag.',
        },
      ],
      warnings: [],
    };
  }
  const cacheKey = hashContent(fileMap, entryPoint);

  // Check cache
  const cached = buildCache.get(cacheKey);
  if (cached) return cached;

  // Get package.json for import map
  const pkgContent = fileMap['/package.json'] || '';
  const importMap = buildImportMap(pkgContent);
  augmentImportMapFromSourceFiles(fileMap, importMap.imports);
  // hasTailwind was already computed above, before Tailwind directive stripping.
  const hasAngular = detectHasAngular(fileMap);
  const isAngular = options.framework === 'angular' || hasAngular;
  // Only use React's automatic JSX transform for React-like frameworks.
  // Vue/Svelte use their own template compilers; vanilla/angular don't need JSX.
  const fw = options.framework || 'react';
  const isReactLike = fw === 'react' || fw === 'unknown';

  // Build config shared across retry attempts
  const buildConfig = () => ({
    entryPoints: [entryPoint],
    bundle: true,
    write: false,
    outdir: '/',
    format: 'esm' as const,
    platform: 'browser' as const,
    target: 'es2020',
    jsx: isReactLike ? ('automatic' as const) : undefined,
    jsxImportSource: isReactLike ? 'react' : undefined,
    external: [
      'react',
      'react-dom',
      'react-dom/client',
      'react/jsx-runtime',
      'react/jsx-dev-runtime',
      'react-router-dom',
      'react-router',
      'lucide-react',
      'next-themes',
    ],
    ...(isAngular && {
      tsconfigRaw: JSON.stringify({
        compilerOptions: {
          experimentalDecorators: true,
          emitDecoratorMetadata: false,
          useDefineForClassFields: false,
        },
      }),
    }),
    loader: {
      '.ts': 'ts' as const,
      '.tsx': 'tsx' as const,
      '.jsx': 'jsx' as const,
      '.js': 'js' as const,
      '.css': 'css' as const,
      '.json': 'json' as const,
      '.svg': 'dataurl' as const,
      '.png': 'dataurl' as const,
      '.jpg': 'dataurl' as const,
      '.jpeg': 'dataurl' as const,
      '.gif': 'dataurl' as const,
      '.webp': 'dataurl' as const,
      '.avif': 'dataurl' as const,
      '.ico': 'dataurl' as const,
      '.woff': 'dataurl' as const,
      '.woff2': 'dataurl' as const,
      '.ttf': 'dataurl' as const,
      '.otf': 'dataurl' as const,
      '.eot': 'dataurl' as const,
      '.mp4': 'dataurl' as const,
      '.webm': 'dataurl' as const,
      '.ogg': 'dataurl' as const,
      '.mp3': 'dataurl' as const,
      '.wav': 'dataurl' as const,
      '.wasm': 'binary' as const,
    },
    // Fresh plugin instances per build (closures read from fileMap which may be mutated between retries)
    plugins: [frameworkTransformPlugin(fileMap), virtualFsPlugin(fileMap), cdnResolvePlugin()],
    logLevel: 'silent' as const,
    sourcemap: 'inline' as const,
  });

  // ── Build with auto-retry on parse errors ──
  // If esbuild fails with parse errors, we parse the error messages, comment out
  // or fix the problematic lines in fileMap, and retry. This catches errors that
  // the repair pipeline in virtualFsPlugin couldn't handle (mid-line issues,
  // context-dependent patterns, errors introduced by other repairs, etc.).
  const MAX_BUILD_RETRIES = 5;
  let result: Awaited<ReturnType<typeof esbuild.build>> | null = null;
  let lastBuildError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_BUILD_RETRIES; attempt++) {
    try {
      result = await esbuild.build(buildConfig());
      lastBuildError = null;
      break;
    } catch (err) {
      lastBuildError = err instanceof Error ? err : new Error(String(err));
      if (attempt < MAX_BUILD_RETRIES) {
        const fullErrorText = formatEsbuildErrorsForAutofix(err);
        const fixed = ENABLE_PREVIEW_SOURCE_FALLBACKS
          ? autoFixBuildErrors(fileMap, fullErrorText)
          : false;
        if (!fixed) break; // Can't parse/fix errors, stop retrying
        console.debug(`[bundler] Auto-fix attempt ${attempt + 1}: retrying build...`);
      }
    }
  }

  if (!result) {
    return {
      html: '',
      errors: [{ text: lastBuildError?.message ?? 'Unknown build error' }],
      warnings: [],
    };
  }

  try {
    // Extract JS and CSS from output files
    let bundledJs = '';
    let bundledCss = '';
    for (const file of result.outputFiles || []) {
      if (file.path.endsWith('.css')) {
        bundledCss += file.text;
      } else {
        bundledJs += file.text;
      }
    }

    // Auto-fix bare React usage in AI-generated code.
    // With jsx: 'automatic', esbuild handles JSX without React in scope, but AI code
    // frequently uses React.useState(), React.forwardRef(), React.createContext(), etc.
    // without importing React — these are bare global references that cause ReferenceError.
    {
      const hasReactNamespace =
        /import\s+\*\s+as\s+React\s+from\s/.test(bundledJs) ||
        /import\s+React[\s,]+from\s/.test(bundledJs) ||
        /(?:var|let|const)\s+React\s*=/.test(bundledJs);
      // Check for React.* usage (but NOT ReactDOM, ReactRouter, etc.)
      if (
        !hasReactNamespace &&
        /\bReact\.(createElement|useState|useEffect|useRef|useCallback|useMemo|useContext|useReducer|useLayoutEffect|useId|forwardRef|createContext|createRef|memo|lazy|Suspense|Fragment|Children|cloneElement|isValidElement|startTransition|useTransition|useDeferredValue|useImperativeHandle|useDebugValue|useSyncExternalStore|Component|PureComponent|StrictMode)\b/.test(
          bundledJs,
        )
      ) {
        bundledJs = `import * as React from "react";\n${bundledJs}`;
      }

      // NOTE: Mangled React detection (React2, React3, React6, etc.) is now done
      // AFTER all React processing (default→namespace rewrites, CDN normalization, etc.)
      // to catch any new mangled names introduced by those transforms. See below.
    }

    // Auto-fix ReactDOM usage in AI-generated code for React 19 compatibility.
    // React 19 moved createRoot/hydrateRoot to "react-dom/client" and removed render/hydrate.
    // createPortal/flushSync remain in "react-dom". Neither has a default export in ESM.
    //
    // Step 1: Always rewrite ReactDOM.createRoot/hydrateRoot/render to bare named imports
    //         (these are react-dom/client APIs regardless of how ReactDOM was imported).
    // Step 2: If ReactDOM is still referenced (createPortal, flushSync, etc.) and NOT
    //         already imported via namespace or default, inject `import * as ReactDOM from "react-dom"`.
    //         If a default import exists (import ReactDOM from "react-dom"), the
    //         default→namespace rewrite below will convert it to namespace automatically.
    {
      // Rewrite ReactDOM.createRoot/hydrateRoot/render UNCONDITIONALLY — these APIs
      // are always wrong when accessed via ReactDOM namespace in React 19.
      const hadReactDomClient = /\bReactDOM\.(createRoot|hydrateRoot|render)\b/.test(bundledJs);
      bundledJs = bundledJs.replace(/\bReactDOM\.(createRoot|hydrateRoot)\b/g, '$1');
      // Rewrite ReactDOM.render(jsx, el) → createRoot(el).render(jsx)
      // React 19 removed ReactDOM.render entirely.
      // IMPORTANT: Can't use simple regex because the first arg (JSX) often contains
      // nested commas from compiled JSX calls like jsx(App, {}). A naive regex splits
      // at the first comma, corrupting the output and causing React error #31
      // ("Objects are not valid as a React child: [object HTMLDivElement]").
      // Instead, use a balanced-paren parser to find the correct top-level comma.
      {
        const renderRe = /\bReactDOM\.render\s*\(/g;
        let renderMatch;
        while ((renderMatch = renderRe.exec(bundledJs)) !== null) {
          const startIdx = renderMatch.index + renderMatch[0].length;
          // Find matching closing paren by counting depth
          let depth = 1;
          let i = startIdx;
          while (i < bundledJs.length && depth > 0) {
            const ch = bundledJs[i];
            if (ch === '(') depth++;
            else if (ch === ')') depth--;
            else if (ch === '"' || ch === "'" || ch === '`') {
              const q = ch;
              i++;
              while (i < bundledJs.length) {
                if (bundledJs[i] === '\\') {
                  i++;
                } else if (bundledJs[i] === q) break;
                i++;
              }
            }
            if (depth > 0) i++;
          }
          if (depth !== 0) continue; // unbalanced
          const endIdx = i; // points to the closing )
          const argsStr = bundledJs.substring(startIdx, endIdx);
          // Find the LAST top-level comma (depth 0) — that separates jsx from container
          let splitDepth = 0;
          let commaIdx = -1;
          for (let j = argsStr.length - 1; j >= 0; j--) {
            const ch = argsStr[j];
            if (ch === ')' || ch === ']' || ch === '}') splitDepth++;
            else if (ch === '(' || ch === '[' || ch === '{') splitDepth--;
            else if (ch === ',' && splitDepth === 0) {
              commaIdx = j;
              break;
            }
          }
          if (commaIdx === -1) continue;
          const jsxArg = argsStr.substring(0, commaIdx).trim();
          const elArg = argsStr.substring(commaIdx + 1).trim();
          const replacement = `createRoot(${elArg}).render(${jsxArg})`;
          bundledJs =
            bundledJs.substring(0, renderMatch.index) +
            replacement +
            bundledJs.substring(endIdx + 1);
          renderRe.lastIndex = renderMatch.index + replacement.length;
        }
      }

      // Inject createRoot/hydrateRoot if we just rewrote ReactDOM.createRoot → createRoot.
      // Use globalThis.__TASKING_createRoot which is pre-loaded by the assembleHtml wrapper
      // script (which CAN use the import map, unlike blob URL modules).
      if (hadReactDomClient) {
        const needsCreateRoot = /\bcreateRoot\b/.test(bundledJs);
        const needsHydrateRoot = /\bhydrateRoot\b/.test(bundledJs);
        if (needsCreateRoot || needsHydrateRoot) {
          const hasCreateRootDecl =
            /import\s*\{[^}]*\bcreateRoot\b[^}]*\}\s*from\s/.test(bundledJs) ||
            /(?:var|let|const)\s+createRoot\s*=/.test(bundledJs);
          if (!hasCreateRootDecl) {
            const decls: string[] = [];
            if (needsCreateRoot) decls.push('var createRoot = globalThis.__TASKING_createRoot;');
            if (needsHydrateRoot) decls.push('var hydrateRoot = globalThis.__TASKING_hydrateRoot;');
            bundledJs = `${decls.join('\n')}\n${bundledJs}`;
          }
        }
      }

      // If ReactDOM is still referenced (createPortal, flushSync, etc.) and not imported, inject it.
      // Skip if already imported via namespace (`import * as ReactDOM`) or default (`import ReactDOM from`).
      // Default imports will be fixed to namespace by the default→namespace rewrite pass below.
      const hasReactDomImport =
        /import\s+\*\s+as\s+ReactDOM\s+from\s/.test(bundledJs) ||
        /import\s+ReactDOM[\s,]+from\s/.test(bundledJs) ||
        /(?:var|let|const)\s+ReactDOM\s*=/.test(bundledJs);
      if (!hasReactDomImport && /\bReactDOM\b/.test(bundledJs)) {
        bundledJs = `import * as ReactDOM from "react-dom";\n${bundledJs}`;
      }
    }

    // Auto-inject react-router-dom imports for common components/hooks that are
    // referenced in the bundle but not imported. AI-generated code frequently uses
    // <BrowserRouter>, <Routes>, <Route> etc. as JSX elements without importing them.
    // esbuild preserves these as bare variable references → ReferenceError at runtime.
    {
      const ROUTER_IDENTIFIERS = [
        'BrowserRouter',
        'HashRouter',
        'MemoryRouter',
        'Routes',
        'Route',
        'Link',
        'NavLink',
        'Navigate',
        'Outlet',
        'useNavigate',
        'useLocation',
        'useParams',
        'useSearchParams',
        'useRoutes',
        'useMatch',
        'useOutlet',
        'useOutletContext',
        'RouterProvider',
        'createBrowserRouter',
        'createHashRouter',
        'createMemoryRouter',
        'createRoutesFromElements',
      ];
      const hasRouterImport = /import\s[\s\S]{0,500}?from\s*["']react-router(?:-dom)?["']/.test(
        bundledJs,
      );
      if (!hasRouterImport) {
        // Only inject identifiers that aren't already declared in the bundle
        const usedIds = ROUTER_IDENTIFIERS.filter((id) => {
          if (!new RegExp(`\\b${id}\\b`).test(bundledJs)) return false;
          if (new RegExp(`(?:function|var|let|const|class)\\s+${id}\\b`).test(bundledJs)) return false;
          return true;
        });
        if (usedIds.length > 0) {
          bundledJs = `import { ${usedIds.join(', ')} } from "react-router-dom";\n${bundledJs}`;
        }
      }
    }

    // Undeclared component safety net — scans for PascalCase identifiers used as
    // first argument to ANY function call: (PascalName, ...). This catches ALL
    // esbuild JSX output patterns including the comma-operator form:
    //   (0, import_jsx_runtime.jsx)(LoadingSpinner, {})
    // which no jsx-specific regex can match since "jsx" is a property access.
    {
      const usedComponents = new Set<string>();
      let jm;
      // Universal pattern: any PascalCase name appearing as (PascalName, ...)
      // This catches jsx(), _jsx(), createElement(), and (0, x.jsx)() patterns
      const callArgRe = /\(\s*([A-Z][A-Za-z][A-Za-z0-9]*)\s*,/g;
      while ((jm = callArgRe.exec(bundledJs)) !== null) usedComponents.add(jm[1]);

      // Also scan for PascalCase identifiers used as values (not just JSX call args).
      // Catches: icon: Mail, icon={Mail}, [Mail, Send], etc.
      // Only match identifiers that look like component/icon names (3+ chars, PascalCase)
      // Strip string literals first so PascalCase words inside strings
      // (like "Web Development", "San Francisco") aren't mistaken for components.
      // FIRST strip base64 data URLs (very long strings that can cause regex
      // catastrophic backtracking and leave base64 substrings visible to bareRefRe).
      const codeNoStrings = bundledJs
        .replace(/data:[a-zA-Z]+\/[a-zA-Z0-9+\-.]+;base64,[A-Za-z0-9+/=\r\n]+/g, '"<BASE64>"')
        .replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`/gs, '""');
      const bareRefRe = /\b([A-Z][a-z][A-Za-z0-9]+)\b/g;
      while ((jm = bareRefRe.exec(codeNoStrings)) !== null) {
        const name = jm[1];
        // Skip common JS globals and type names
        if (
          [
            'Object',
            'Array',
            'String',
            'Number',
            'Boolean',
            'Symbol',
            'Error',
            'Promise',
            'RegExp',
            'Date',
            'Math',
            'JSON',
            'Map',
            'Set',
            'Proxy',
            'Reflect',
            'Intl',
            'WeakMap',
            'WeakSet',
            'Int8Array',
            'Uint8Array',
            'Float32Array',
            'Float64Array',
            'ArrayBuffer',
            'DataView',
            'TypeError',
            'RangeError',
            'SyntaxError',
            'ReferenceError',
            'Event',
            'Element',
            'Document',
            'Window',
            'Node',
            'Response',
            'Request',
            'Headers',
            'FormData',
            'AbortController',
            'Navigator',
            'Performance',
            'Console',
            'Storage',
            'Location',
            'History',
            'HTMLElement',
            'SVGElement',
            'XMLHttpRequest',
            'WebSocket',
            // React built-ins
            'React',
            'ReactDOM',
            'Component',
            'Fragment',
            'Suspense',
            'StrictMode',
            'Provider',
            'Consumer',
            'Infinity',
            'Function',
            'Generator',
            'Iterator',
          ].includes(name)
        )
          continue;
        usedComponents.add(name);
      }

      // Scaffold-provided component names — these should be resolved by esbuild
      // from the virtual FS, not stubbed. Stubbing them hides import resolution bugs.
      const SCAFFOLD_NAMES = new Set([
        'Button', 'Card', 'CardHeader', 'CardTitle', 'CardDescription',
        'CardContent', 'CardFooter', 'Input', 'Label', 'Badge', 'Container',
        'Separator', 'Textarea', 'Skeleton', 'Avatar', 'AvatarImage',
        'AvatarFallback', 'Checkbox', 'Dialog', 'DialogOverlay', 'DialogContent',
        'DialogHeader', 'DialogFooter', 'DialogTitle', 'DialogDescription',
        'Tabs', 'TabsList', 'TabsTrigger', 'TabsContent', 'Header',
        'ThemeProvider', 'ThemeToggle', 'Sidebar', 'ToastContainer', 'ToastProvider',
      ]);
      const stubs: string[] = [];
      for (const name of usedComponents) {
        // Skip scaffold components and their mangled variants (e.g. Card2, Button3)
        if (SCAFFOLD_NAMES.has(name)) continue;
        const baseName = name.replace(/\d+$/, '');
        if (baseName !== name && SCAFFOLD_NAMES.has(baseName)) continue;
        // Skip if declared by var/const/let/function/class
        if (new RegExp(`\\b(?:var|const|let|function|class)\\s+${name}\\b`).test(bundledJs))
          continue;
        // Skip if imported — check each import statement individually
        {
          const importRe = /import\s+\{([^}]*)\}\s+from\s/g;
          let importMatch;
          let isImported = false;
          while ((importMatch = importRe.exec(bundledJs)) !== null) {
            if (new RegExp(`\\b${name}\\b`).test(importMatch[1])) {
              isImported = true;
              break;
            }
          }
          if (isImported) continue;
        }
        stubs.push(`var ${name} = function(p){return (p&&p.children)||null;};`);
      }
      if (stubs.length > 0) {
        previewVerboseLog(
          `[bundler] Safety net: stubbing ${stubs.length} undeclared components:`,
          stubs.map((s) => s.match(/var (\w+)/)?.[1]).filter(Boolean),
        );
        bundledJs = stubs.join('\n') + '\n' + bundledJs;
      } else if (usedComponents.size > 0) {
        previewVerboseLog(
          `[bundler] Safety net: found ${usedComponents.size} PascalCase refs, all declared:`,
          [...usedComponents].slice(0, 10),
        );
      }
    }

    // Auto-inject next-themes imports for ThemeProvider/useTheme that are referenced
    // but not imported. Only inject if ThemeProvider is NOT already declared or imported.
    {
      const THEME_IDENTIFIERS = ['ThemeProvider', 'useTheme'];
      const hasAnyThemeImport =
        /import\s[\s\S]{0,500}?ThemeProvider[\s\S]{0,500}?from\s*["'][^"']+["']/.test(bundledJs) ||
        /import\s[\s\S]{0,500}?from\s*["']next-themes(?:\/react)?["']/.test(bundledJs);
      // Also check if ThemeProvider is already declared as a function/var/const in the bundle
      const hasThemeDeclaration =
        /(?:function|var|let|const)\s+ThemeProvider\b/.test(bundledJs);
      if (!hasAnyThemeImport && !hasThemeDeclaration) {
        const usedIds = THEME_IDENTIFIERS.filter((id) => new RegExp(`\\b${id}\\b`).test(bundledJs));
        if (usedIds.length > 0) {
          bundledJs = `import { ${usedIds.join(', ')} } from "next-themes";\n${bundledJs}`;
        }
      }
    }

    // Normalize all React imports back to bare specifiers so they always flow through
    // the import-map shim in assembleHtml (default + full named exports).
    const canonicalReactUrl = 'react';
    const canonicalReactDomUrl = 'react-dom';
    const canonicalReactDomClientUrl = 'react-dom/client';

    bundledJs = bundledJs.replace(/from\s*["']react["']/g, `from "${canonicalReactUrl}"`);
    bundledJs = bundledJs.replace(
      /import\(\s*["']react["']\s*\)/g,
      `import("${canonicalReactUrl}")`,
    );
    // Normalize react-dom/client BEFORE plain react-dom (longer match first)
    bundledJs = bundledJs.replace(
      /from\s*["']react-dom\/client["']/g,
      `from "${canonicalReactDomClientUrl}"`,
    );
    bundledJs = bundledJs.replace(
      /import\(\s*["']react-dom\/client["']\s*\)/g,
      `import("${canonicalReactDomClientUrl}")`,
    );
    // Normalize plain react-dom (must come AFTER react-dom/client to avoid partial match)
    bundledJs = bundledJs.replace(/from\s*["']react-dom["']/g, `from "${canonicalReactDomUrl}"`);
    bundledJs = bundledJs.replace(
      /import\(\s*["']react-dom["']\s*\)/g,
      `import("${canonicalReactDomUrl}")`,
    );

    // Rewrite absolute CDN React paths to bare specifiers so they cannot bypass the shim.
    bundledJs = bundledJs.replace(
      /from\s*["']https?:\/\/[^"']*\/react(?:@|\/)[^"']*["']/g,
      `from "${canonicalReactUrl}"`,
    );
    bundledJs = bundledJs.replace(
      /import\(\s*["']https?:\/\/[^"']*\/react(?:@|\/)[^"']*["']\s*\)/g,
      `import("${canonicalReactUrl}")`,
    );
    // Rewrite CDN react-dom/client paths BEFORE plain react-dom (longer match first)
    bundledJs = bundledJs.replace(
      /from\s*["']https?:\/\/[^"']*\/react-dom(?:@|\/)[^"']*\/client[^"']*["']/g,
      `from "${canonicalReactDomClientUrl}"`,
    );
    bundledJs = bundledJs.replace(
      /import\(\s*["']https?:\/\/[^"']*\/react-dom(?:@|\/)[^"']*\/client[^"']*["']\s*\)/g,
      `import("${canonicalReactDomClientUrl}")`,
    );
    // Rewrite CDN react-dom paths (plain, without /client)
    bundledJs = bundledJs.replace(
      /from\s*["']https?:\/\/[^"']*\/react-dom(?:@[^"'/]*)?(?:\?[^"']*)?\s*["']/g,
      `from "${canonicalReactDomUrl}"`,
    );
    bundledJs = bundledJs.replace(
      /import\(\s*["']https?:\/\/[^"']*\/react-dom(?:@[^"'/]*)?(?:\?[^"']*)?\s*["']\s*\)/g,
      `import("${canonicalReactDomUrl}")`,
    );

    // ESM React has no default export; rewrite defaults to namespace imports.
    // IMPORTANT: Handle mixed imports FIRST (import React, { useState } from "react")
    // before the simple default import pattern, or the mixed pattern gets partially matched.
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s*,\s*\{([^}]+)\}\s+from\s*["']react["']\s*;?/g,
      (_, defaultName, namedExports) =>
        `import * as ${defaultName} from "react";\nconst { ${namedExports.trim()} } = ${defaultName};`,
    );
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s*,\s*\{([^}]+)\}\s+from\s*["']react-dom\/client["']\s*;?/g,
      (_, defaultName, namedExports) =>
        `import * as ${defaultName} from "react-dom/client";\nconst { ${namedExports.trim()} } = ${defaultName};`,
    );
    // Handle mixed imports for plain react-dom: import ReactDOM, { createPortal } from "react-dom"
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s*,\s*\{([^}]+)\}\s+from\s*["']react-dom["']\s*;?/g,
      (_, defaultName, namedExports) =>
        `import * as ${defaultName} from "react-dom";\nconst { ${namedExports.trim()} } = ${defaultName};`,
    );
    // Rewrite default imports to namespace: react-dom/client BEFORE react-dom (longer first)
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s+from\s*["'][^"']*react-dom\/client(?:\/es\d+\/client\.mjs)?["']\s*;?/g,
      (_, name) => `import * as ${name} from "${canonicalReactDomClientUrl}";`,
    );
    // Rewrite default imports for plain react-dom
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s+from\s*["']react-dom["']\s*;?/g,
      (_, name) => `import * as ${name} from "${canonicalReactDomUrl}";`,
    );
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s+from\s*["'][^"']*react(?:\/es\d+\/react\.mjs)?["']\s*;?/g,
      (_, name) => `import * as ${name} from "${canonicalReactUrl}";`,
    );
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s+from\s*["']react["']\s*;?/g,
      (_, name) => `import * as ${name} from "react";`,
    );
    bundledJs = bundledJs.replace(
      /import\s+(\w+)\s+from\s*["']react-dom\/client["']\s*;?/g,
      (_, name) => `import * as ${name} from "react-dom/client";`,
    );

    // Force next-themes to bare specifier so the import-map ThemeProvider shim is always used in preview.
    // CDN (esm.sh) can fail in iframes or not export ThemeProvider correctly.
    const canonicalNextThemes = 'next-themes/react';
    bundledJs = bundledJs.replace(
      /from\s*["'](?:https?:\/\/[^"']*\/)?next-themes(?:\/[^"']*)?["']/g,
      `from "${canonicalNextThemes}"`,
    );
    bundledJs = bundledJs.replace(
      /import\(\s*["'](?:https?:\/\/[^"']*\/)?next-themes(?:\/[^"']*)?["']\s*\)/g,
      `import("${canonicalNextThemes}")`,
    );

    // Safety net: ensure createRoot/hydrateRoot are available if referenced as bare calls.
    // After the ReactDOM.createRoot → createRoot rewrite above, the original namespace
    // import (import * as ReactDOM from "react-dom/client") no longer provides createRoot
    // as a bare identifier.
    //
    // Strategy: use globalThis.__TASKING_createRoot which is pre-loaded by the wrapper
    // script in assembleHtml (which CAN use the import map, unlike blob URL modules).
    // Also keep a static import as fallback for cases where the URL is fully resolved.
    {
      const hasCreateRootDecl2 =
        /import\s*\{[^}]*\bcreateRoot\b[^}]*\}\s*from\s/.test(bundledJs) ||
        /(?:var|let|const)\s+createRoot\s*=/.test(bundledJs);
      if (!hasCreateRootDecl2 && /\bcreateRoot\s*\(/.test(bundledJs)) {
        // Inject globalThis fallback first, then static import as backup
        bundledJs = `var createRoot = globalThis.__TASKING_createRoot;\n${bundledJs}`;
      }
      const hasHydrateRootDecl2 =
        /import\s*\{[^}]*\bhydrateRoot\b[^}]*\}\s*from\s/.test(bundledJs) ||
        /(?:var|let|const)\s+hydrateRoot\s*=/.test(bundledJs);
      if (!hasHydrateRootDecl2 && /\bhydrateRoot\s*\(/.test(bundledJs)) {
        bundledJs = `var hydrateRoot = globalThis.__TASKING_hydrateRoot;\n${bundledJs}`;
      }
    }

    // Replace ALL static imports from react-dom/client with globalThis assignments.
    // Blob URL modules cannot use the document's import map, so static imports fail.
    // The wrapper script in assembleHtml pre-loads these via dynamic import (which
    // CAN use the import map) and stores them on globalThis.
    bundledJs = bundledJs.replace(
      /import\s*\{([^}]*)\}\s*from\s*["'](?:[^"']*\/)?react-dom(?:@[^/"']*)?\/client[^"']*["']\s*;?/g,
      (match, names: string) => {
        const parts = names
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        return parts
          .map((part) => {
            const [imported, , local] = part.split(/\s+/);
            const varName = local || imported;
            if (imported === 'createRoot')
              return `var ${varName} = globalThis.__TASKING_createRoot;`;
            if (imported === 'hydrateRoot')
              return `var ${varName} = globalThis.__TASKING_hydrateRoot;`;
            return `var ${varName} = globalThis.__TASKING_${imported};`;
          })
          .join('\n');
      },
    );
    // Also replace namespace imports: import * as X from "react-dom/client"
    bundledJs = bundledJs.replace(
      /import\s*\*\s*as\s+(\w+)\s+from\s*["'](?:[^"']*\/)?react-dom(?:@[^/"']*)?\/client[^"']*["']\s*;?/g,
      (_, name) =>
        `var ${name} = { createRoot: globalThis.__TASKING_createRoot, hydrateRoot: globalThis.__TASKING_hydrateRoot };`,
    );

    // Mangled React safety-net: esbuild with bundle:true may rename React to
    // React2, React3, React6, etc. when merging multiple modules. These mangled
    // names may not have a corresponding import after all the rewrites above
    // (default→namespace, CDN normalization, import-vs-var conflict resolution).
    // Inject var declarations from globalThis.__TASKING_React for any undeclared ones.
    // This runs LAST to catch names introduced by earlier transforms.
    {
      const mangledReactRefs = bundledJs.match(/\bReact\d+\./g);
      if (mangledReactRefs) {
        const mangledNames = new Set(mangledReactRefs.map((r) => r.slice(0, -1)));
        const injectLines: string[] = [];

        for (const name of mangledNames) {
          const hasDecl =
            bundledJs.includes(`import * as ${name} `) ||
            bundledJs.includes(`import ${name} `) ||
            bundledJs.includes(`import ${name},`) ||
            new RegExp(`\\b(?:var|let|const)\\s+${name}\\s*=`).test(bundledJs);

          if (!hasDecl) {
            injectLines.push(`var ${name} = globalThis.__TASKING_React;`);
          }
        }

        if (injectLines.length > 0) {
          bundledJs = injectLines.join('\n') + '\n' + bundledJs;
        }
      }
    }

    // Final safety-net pass: ensure any remaining bare imports in the output bundle
    // are present in the import map used by the iframe.
    augmentImportMapFromBundledJs(bundledJs, importMap.imports);

    // Inline virtual-FS asset paths: replace string occurrences of known asset paths
    // (e.g. "/assets/hero.png") with their actual data URL so that <img src="...">
    // references work inside srcdoc iframes without hitting the production server.
    // This covers AI-generated code that writes `<img src="/assets/foo.png">` directly
    // rather than using ESM imports (which the virtual-FS plugin already handles).
    {
      const ASSET_EXTS = /\.(png|jpe?g|gif|webp|svg|mp4|webm|ogg|mp3|wav|pdf)$/i;
      for (const [filePath, content] of Object.entries(fileMap)) {
        if (!content || !content.startsWith('data:') || !ASSET_EXTS.test(filePath)) continue;

        // Validate base64 data URLs before inlining to prevent corrupted assets from breaking the bundle
        const isValidDataUrl = (dataUrl: string): boolean => {
          try {
            // Check format: data:[<mime>][;charset=utf-8][;base64],<data>
            const match = dataUrl.match(/^data:([^;]*)(;[^,]*)*,(.+)$/);
            if (!match) return false;
            const [, , , data] = match;
            // For base64, validate that it contains only valid base64 chars
            if (dataUrl.includes(';base64,')) {
              // Base64 should only contain: A-Z, a-z, 0-9, +, /, =, and whitespace
              return /^[A-Za-z0-9+/\n\r\s]*={0,2}$/.test(data);
            }
            return true;
          } catch {
            return false;
          }
        };

        if (!isValidDataUrl(content)) {
          console.warn(`[bundler] Skipping invalid data URL asset: ${filePath}`);
          continue;
        }

        // Escape regex-special chars in the path
        const escaped = filePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Replace both double-quoted and single-quoted path occurrences
        const pathRe = new RegExp(`(["'])${escaped}\\1`, 'g');
        const prev = bundledJs;
        bundledJs = bundledJs.replace(pathRe, () => JSON.stringify(content));
        if (bundledJs !== prev) {
          previewVerboseLog(`[bundler] Inlined asset URL: ${filePath}`);
        }
      }
    }

    // Find index.html template
    const indexHtml =
      fileMap['/index.html'] ||
      `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body><div id="root"></div><div id="app"></div></body></html>`;

    const html = assembleHtml({
      indexHtml,
      bundledJs,
      bundledCss,
      importMap: importMap.imports,
      hasTailwind,
      hasAngular,
      framework: options.framework,
    });

    const bundleResult: BundleResult = {
      html,
      errors: result.errors.map((e) => ({ text: e.text })),
      warnings: result.warnings.map((w) => ({ text: w.text })),
    };

    // Cache (limit cache size)
    if (buildCache.size > 20) {
      const firstKey = buildCache.keys().next().value;
      if (firstKey) buildCache.delete(firstKey);
    }
    buildCache.set(cacheKey, bundleResult);

    return bundleResult;
  } catch (err) {
    return {
      html: '',
      errors: [{ text: err instanceof Error ? err.message : String(err) }],
      warnings: [],
    };
  }
}

/** Check if session files need esbuild bundling (have JSX/TSX/Vue/Svelte) */
export function needsEsbuildBundling(files: Array<{ fileName: string }>): boolean {
  return files.some(
    (f) =>
      f.fileName.endsWith('.tsx') ||
      f.fileName.endsWith('.jsx') ||
      f.fileName.endsWith('.vue') ||
      f.fileName.endsWith('.svelte'),
  );
}
